/**
 * Client Detail — one client, all their projects (IA FIX 2026-05-21).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  AVBP_DATA_MODEL_IA_SPEC_v1 §5 / Q5 — the screen the rebuild was missing.
 * ─────────────────────────────────────────────────────────────────────────
 *  Reached from the Clients list (`/client/:clientId`). Shows:
 *    · the client master record — contact details (Q5)
 *    · the list of THAT client's projects — every project whose `clientId`
 *      matches (Q5: a client owns N projects)
 *    · money rolled up across all the client's projects (Q5)
 *    · a "+ New Project for this client" action (§8(3) — mints a fresh
 *      VP###, attaches the existing clientId, no new client number)
 *
 *  Conversation stays per-project (spec §5/§6) — each project row opens
 *  Project Detail / the project Conversation. This screen is the
 *  client→projects hierarchy that sits ABOVE the chat-first project view.
 *
 *  Client never reaches this route (RequireAuth roles gate). Prior art:
 *  src/_legacy/routes/ClientDetail.tsx (pre-rebuild) — its shape informed
 *  this; the code conforms to the CURRENT design system (Box/Card/Btn3).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { addDoc, collection, getDocs, orderBy, query as fsQuery, serverTimestamp } from 'firebase/firestore';
import ContactCommsTab from '../components/ContactCommsTab';
import IntakeLinkSendBar from '../components/IntakeLinkSendBar';
import { ContactTabBar, type ContactTabKey } from '../components/ContactTabBar';
import { useAuth } from '../lib/auth';
import { usePageBreadcrumb } from '../lib/pageChromeContext';
import { useToast } from '../components/Toast';
import { visibilityFor } from '../lib/roles';
import { Box, Btn3, Card, EmptyNote, Tag } from '../components/ui';
import { RelinkProjectPanel } from '../components/RelinkProject';
import {
  inr,
  moneyViewOf,
  templateOf,
  type DemoProject,
} from '../lib/trackDemo';
import {
  LEAD_STATE_LABEL,
  LEAD_SOURCE_LABEL,
  type Lead,
} from '../lib/leads';
import { canonicalServiceNameFor, canonicalTierOptions } from '../lib/leadQuote';
import { getLead } from '../lib/leadStore';
import {
  resolveAstroGroup,
  renderAstroPdf,
  type AstroGroupResolved,
} from '../lib/astroHome';
import { CORE_TIERS, type CoreTier } from '../lib/engagementTemplate';
import {
  addParticipant,
  archiveClient,
  clientTrashBlock,
  contactStatus,
  createProjectForClient,
  getClient,
  listenClients,
  listenPartners,
  listenProjects,
  mergeClients,
  projectLabel,
  projectsForClient,
  saveEmails,
  savePhones,
  saveRoleTags,
  trashClient,
  updateClient,
  updateContactPersonData,
  type LiveClient,
  type LivePartner,
  type LiveProject,
} from '../lib/projectStore';
import {
  migratePhoneField,
  participantLabel,
  pobDisplayName,
  type ContactAddress,
  type EmailEntry,
  type ParticipantRole,
  type PhoneEntry,
  type PlaceOfBirth,
} from '../lib/contactModel';
import { ASSIGNABLE_CONTACT_ROLES } from '../lib/p1Roles';
import { ClientPicker } from '../components/ClientPicker';
import { geocode, countryCodeHint, type CitySearchHit } from '../lib/geocoder';
import { parseCoordsString } from '../lib/geocode';
import { CityAutocomplete } from '../components/CityAutocomplete';
import { maskTob, isCompleteTob } from '../lib/tobMask';
import { canManageTrash, canMergeClients } from '../lib/trash';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import {
  hasDeliveryChannel,
  interpretSendAccess,
  pickAccessHandle,
  type SendLinkResp,
} from '../lib/sendClientAccess';
import {
  addMemberToFamily,
  listenFamilies,
  removeMemberFromFamily,
  type FamilyGroup,
  type FamilyMember,
} from '../lib/familyStore';

/** A LiveProject → the DemoProject shape the track helpers consume. */
function liveToRow(p: LiveProject): DemoProject {
  return {
    pid: p.pid,
    vp: p.vp,
    projectName: p.projectName,
    clientName: p.clientName,
    clientNo: p.clientNo,
    tier: p.tierCode,
    activeStageKey: p.activeStageKey,
    assigneeUid: p.assigneeUid,
    assigneeName: p.assigneeName,
    valueINR: p.valueINR,
    clearedGateIds: p.clearedGateIds,
    openProposals: 0,
  };
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '14px 0 8px',
      }}
    >
      {children}
    </div>
  );
}

/** Up-to-2-letter initials for the header avatar. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** "12 Jan 2024" — locale-safe; '' when unparseable/absent. */
function fmtDayMonthYear(iso?: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function ClientDetail() {
  const { clientId = '' } = useParams();
  const cid = decodeURIComponent(clientId);
  const { role, user } = useAuth();
  const { toast } = useToast();
  const vis = visibilityFor(role);
  const actorName = user?.displayName || user?.email?.split('@')[0] || 'You';

  const [client, setClient] = useState<LiveClient | null>(null);
  const [clientLoaded, setClientLoaded] = useState(false);
  const [live, setLive] = useState<LiveProject[]>([]);
  const [creating, setCreating] = useState(false);
  // Which project row's RE-LINK panel is open (item 1).
  const [relinking, setRelinking] = useState<string | null>(null);
  // The live client masters — needed to resolve a Merge target.
  const [masters, setMasters] = useState<LiveClient[]>([]);
  const mayTrash = canManageTrash(role);
  const mayMerge = canMergeClients(role);

  // The client master record.
  useEffect(() => {
    let alive = true;
    getClient(cid)
      .then((c) => {
        if (!alive) return;
        setClient(c);
        setClientLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setClientLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [cid]);

  // The live project stream — filtered to this client.
  useEffect(() => {
    const unsub = listenProjects(setLive, () =>
      toast('Could not load projects (check connection)'),
    );
    return unsub;
  }, [toast]);

  // The client masters — for the Merge-into picker (resolve the survivor).
  useEffect(() => {
    if (!mayMerge) return;
    const unsub = listenClients(setMasters, {
      /* the merge picker just shows fewer options if this fails */
    });
    return unsub;
  }, [mayMerge]);

  const projects = useMemo(
    () => projectsForClient(live, cid),
    [live, cid],
  );
  const visibleProjects = projects.filter((p) => !p.archived);
  const archivedProjects = projects.filter((p) => p.archived);

  // Money rolled up across ALL the client's live projects (Q5).
  const rollup = useMemo(() => {
    let value = 0;
    let paid = 0;
    let due = 0;
    for (const p of visibleProjects) {
      const m = moneyViewOf(liveToRow(p));
      value += m.valueINR;
      paid += m.paidINR;
      due += m.dueINR;
    }
    return { value, paid, due };
  }, [visibleProjects]);

  // ── Tabbed record state (P1 2026-07-06) ──
  // Default tab = Contact details (owner-locked).
  const [activeTab, setActiveTab] = useState<ContactTabKey>('details');
  // The Contact-details editor is LIFTED here so the header "Edit details"
  // button can open it (it otherwise owns its own edit state internally).
  const [contactEditing, setContactEditing] = useState(false);
  // Share-billing-form popover (informational — the form is not built yet).
  const [shareOpen, setShareOpen] = useState(false);
  const headRef = useRef<HTMLElement | null>(null);

  // Pin the tab strip directly BELOW the record header, which pins directly
  // below the app page-chrome (.phead) — mirrors ProjectDetail's --l1a-bar-top.
  // We measure BOTH so the strip's sticky offset stays correct as the header
  // reflows (meta line wraps on narrow widths).
  useEffect(() => {
    const phead = document.querySelector('.phead') as HTMLElement | null;
    const apply = () => {
      const ph = phead?.offsetHeight ?? 0;
      const hd = headRef.current?.offsetHeight ?? 0;
      // The sticky tab strip sits directly under the header; measure it too so
      // the Communications tab can claim the EXACT remaining viewport (single
      // scroll — the thread's own canvas is the sole scroller, no page scroll).
      const strip = document.querySelector(
        '.stagebar.contact-tabs',
      ) as HTMLElement | null;
      const st = strip?.offsetHeight ?? 0;
      // The record column sits inside `.pbody`, whose top padding pushes the
      // header down — it counts toward the space above the comms pane.
      const pbody = document.querySelector('.pbody') as HTMLElement | null;
      const padTop = pbody
        ? parseFloat(getComputedStyle(pbody).paddingTop) || 0
        : 0;
      document.documentElement.style.setProperty('--rec-phead-top', `${ph}px`);
      document.documentElement.style.setProperty('--rec-bar-top', `${ph + hd}px`);
      // Everything above the Communications pane = app chrome + .pbody top
      // padding + record header + tab strip. The pane is height:
      // calc(100dvh - this - mobile-tab-bar), so NEITHER the work area nor
      // .pbody scrolls — the reused thread canvas is the sole scroller.
      document.documentElement.style.setProperty(
        '--rec-comms-top',
        `${ph + padTop + hd + st}px`,
      );
    };
    apply();
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (phead) ro?.observe(phead);
    if (headRef.current) ro?.observe(headRef.current);
    const strip = document.querySelector('.stagebar.contact-tabs');
    if (strip) ro?.observe(strip);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      document.documentElement.style.removeProperty('--rec-phead-top');
      document.documentElement.style.removeProperty('--rec-bar-top');
      document.documentElement.style.removeProperty('--rec-comms-top');
    };
  }, []);

  // Close the share popover on outside-click / Escape.
  useEffect(() => {
    if (!shareOpen) return;
    const onDoc = (ev: MouseEvent) => {
      const wrap = document.querySelector('.share-wrap');
      if (wrap && !wrap.contains(ev.target as Node)) setShareOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setShareOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [shareOpen]);

  // Display name / number — the master record wins, else the project cache.
  const displayName =
    client?.name || projects[0]?.clientName || cid;
  const displayNo = client?.clientNo || projects[0]?.clientNo || cid;

  // Derived Client-vs-Contact status (the SAME helper the directory uses, so
  // the header pill and the Clients tab can never disagree).
  const status = contactStatus(cid, live);

  // ITEM 4 — the client NAME is the prominent heading. It renders as soon
  // as EITHER the client master doc OR the first project (which carries the
  // name as a cache) is available — whichever arrives first — so the header
  // is not blank while the master doc is still in flight. Only the raw id
  // shows if nothing has resolved yet.
  const headerName =
    client?.name || projects[0]?.clientName || (clientLoaded ? cid : '');
  const headerResolving = !headerName;

  // SINGLE breadcrumb (owner fix 2026-07-06) — publish the canonical trail UP
  // into the AppShell `.phead` top-left chrome via the shared page-chrome
  // channel, exactly like ProjectDetail. This replaces the generic route-derived
  // "Manage › Contact" crumb with the useful "Contacts › {name}" path (Contacts
  // links back to the list) AND removes the need for a second inner crumb row on
  // the record — one working breadcrumb, no wasted vertical space. Called BEFORE
  // the not-found early return so the hook is unconditional (rules of hooks).
  usePageBreadcrumb([
    { label: 'Contacts', href: '/contacts' },
    { label: headerResolving ? 'Loading…' : headerName },
  ]);

  // A client id with no master record AND no projects → genuinely unknown.
  if (clientLoaded && !client && projects.length === 0 && live.length > 0) {
    return (
      <Box title="Contact" note={cid}>
        <EmptyNote title="Contact not found">
          No contact record or project exists for &ldquo;{cid}&rdquo;.{' '}
          <Link to="/contacts">Back to Contacts</Link>.
        </EmptyNote>
      </Box>
    );
  }

  const enquiryCount = new Set((client?.leadRefs ?? []).filter(Boolean)).size;

  return (
    <div className="client-detail-layout" style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
    {/* ── Left / main column ── */}
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

      {/* Record breadcrumb now lives in the AppShell top chrome (single crumb,
          published via usePageBreadcrumb above) — the inner duplicate row was
          removed 2026-07-06 to reclaim the vertical space (owner fix). */}

      {/* ── PINNED HEADER (2 lines) — stays above the tab strip ── */}
      <section className="rec-head-card" ref={headRef}>
        <div className="rec-head">
          <div className="avatar" aria-hidden="true">
            {initialsOf(headerResolving ? '?' : headerName)}
          </div>
          <div className="rh-name">
            {headerResolving ? 'Loading contact…' : headerName}
          </div>
          <div className="rh-actions">
            <Btn3
              intent="ink"
              size="sm"
              onClick={() => {
                setActiveTab('details');
                setContactEditing(true);
              }}
              disabled={!client}
              title={client ? undefined : 'No master record to edit yet.'}
            >
              Edit details
            </Btn3>
            <div className="share-wrap">
              <Btn3
                intent="ghost"
                size="sm"
                aria-haspopup="true"
                aria-expanded={shareOpen}
                aria-controls="share-pop"
                onClick={() => setShareOpen((o) => !o)}
              >
                Share billing form ▾
              </Btn3>
              {/* Non-modal disclosure (Vera LOW): labelled by its heading and
                  toggled by the trigger's aria-expanded — no focus trap, no
                  role="dialog" (which would demand focus management). */}
              <div
                className="share-pop"
                id="share-pop"
                role="group"
                aria-labelledby="share-pop-h"
                hidden={!shareOpen}
              >
                <h4 id="share-pop-h">Share the billing form</h4>
                <span className="soon">⏳ Coming — not built yet</span>
                <p className="cap">
                  Billing-form sharing isn’t wired yet. Detailed intake forms are
                  sent from the contact’s project via <b>Send Intake Form</b> (in
                  the Contact details tab), once a service is confirmed.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="rh-meta">
          <span
            className={`meta-pill${status === 'client' ? '' : ' is-contact'}`}
            title={
              status === 'client'
                ? 'Linked to a live project'
                : 'Not linked to a project yet'
            }
          >
            <span className="dot">●</span>{' '}
            {status === 'client' ? 'Client' : 'Contact'}
          </span>
          <span className="msep">·</span> Contact
          <span className="msep">·</span>{' '}
          <strong>{displayNo.startsWith('#') ? displayNo : `#${displayNo}`}</strong>
          {fmtDayMonthYear(client?.createdAt) && (
            <>
              <span className="msep">·</span> added{' '}
              {fmtDayMonthYear(client?.createdAt)}
            </>
          )}
          {client?.referredBy?.name && (
            <>
              <span className="msep">·</span> referred by{' '}
              <strong>{client.referredBy.name}</strong>
            </>
          )}
        </div>
      </section>

      {/* ── TAB STRIP (the L1A StageTabBar mechanism) ── */}
      <ContactTabBar activeKey={activeTab} onSelect={setActiveTab} />

      {/* ── ONE panel that swaps ──
           When the Communications tab is active the panel becomes a fixed-
           height flex column bounded to the remaining viewport, so its inner
           thread canvas is the SOLE scroller and the document does not scroll
           (owner rule: one page scroll, no nested scrollbars). Every OTHER tab
           keeps the default document-scroll behaviour untouched. */}
      <div
        className={`panel-wrap${activeTab === 'comms' ? ' is-comms-pane' : ''}`}
        id="contact-panel"
        role="tabpanel"
        aria-labelledby={`ctab-${activeTab}`}
        tabIndex={0}
      >
        {/* ---- TAB 1 · CONTACT DETAILS ---- */}
        {activeTab === 'details' && (
          <>
            <ClientContact
              cid={cid}
              client={client}
              fallbackName={displayName}
              actorName={actorName}
              editing={contactEditing}
              onEditingChange={setContactEditing}
              onSaved={(c, msg) => {
                setClient(c);
                toast(msg);
              }}
            />

            <hr className="hr" />

            {/* Fix 5 — Notes (kept in Contact details so no save path is lost) */}
            <NotesSection cid={cid} actorName={actorName} />

            <hr className="hr" />

            {/* Contact actions (sign-in link + intake form) + management. */}
            <SendClientAccess cid={cid} client={client} fallbackNo={displayNo} />
            <SendIntakeForm cid={cid} projects={visibleProjects} />

            {client && (mayTrash || mayMerge) && (
              <ManageClient
                client={client}
                liveProjects={live}
                masters={masters}
                mayTrash={mayTrash}
                mayMerge={mayMerge}
                actorName={actorName}
                onChanged={(updated, msg) => {
                  setClient(updated);
                  toast(msg);
                }}
              />
            )}
          </>
        )}

        {/* ---- TAB 2 · BIRTH DETAILS (+ Prakriti + Health) ---- */}
        {activeTab === 'birth' && (
          client ? (
            <PersonalDataCard
              cid={cid}
              client={client}
              liveContacts={masters}
              onSaved={(msg) => toast(msg)}
            />
          ) : (
            <EmptyNote title="No master record yet">
              Birth &amp; personal data is captured on the client master record.
              Convert a lead or create a client to add it.
            </EmptyNote>
          )
        )}

        {/* ---- TAB 3 · GROUPS ---- */}
        {activeTab === 'groups' && (
          <div className="block">
            <p className="sub" style={{ margin: '0 0 var(--s-4)' }}>
              Groups this contact belongs to — a person can belong to several at
              once (families and organisations mixed). Open a group to manage it.
            </p>
            <GroupsSection
              cid={cid}
              contactName={displayName}
              liveContacts={masters}
              actorName={actorName}
            />
          </div>
        )}

        {/* ---- TAB 4 · PROJECTS ---- */}
        {activeTab === 'projects' && (
          <div className="block">
            <h2>
              Projects this contact belongs to{' '}
              <span className="count">
                · {visibleProjects.length} active
                {archivedProjects.length
                  ? ` · ${archivedProjects.length} archived`
                  : ''}
              </span>
            </h2>
            <p className="sub">
              Every project this person is on, with their role and the current
              stage. Each row opens the project.
            </p>

            {vis.seesMoneyInternals && visibleProjects.length > 0 && (
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', margin: '0 0 16px' }}>
                <Stat label="Total value" value={inr(rollup.value)} />
                <Stat label="Paid" value={inr(rollup.paid)} tone="var(--good)" />
                <Stat label="Due" value={inr(rollup.due)} />
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <Btn3 intent="ink" size="sm" onClick={() => setCreating((c) => !c)}>
                {creating ? 'Close' : '+ New project for this client'}
              </Btn3>
            </div>

            {creating && (
              <NewProjectForm
                client={{ clientId: cid, clientNo: displayNo, name: displayName }}
                actorName={actorName}
                onDone={(msg) => {
                  toast(msg);
                  setCreating(false);
                }}
              />
            )}

            {visibleProjects.length === 0 && archivedProjects.length === 0 ? (
              <EmptyNote title="No projects yet">
                This contact has no projects. Use “+ New project for this client”
                above to start one.
              </EmptyNote>
            ) : (
              <div className="grouplist">
                {[...visibleProjects, ...archivedProjects].map((p) => {
                  const row = liveToRow(p);
                  const tpl = templateOf(row);
                  const m = moneyViewOf(row);
                  const roleLabel = participantRoleForContact(p, cid);
                  return (
                    <div key={p.pid}>
                      <Link
                        to={`/project/${p.pid}`}
                        className="glink"
                        style={p.archived ? { opacity: 0.65 } : undefined}
                      >
                        <span className="gicon proj" aria-hidden="true">🏗️</span>
                        <span className="gtext">
                          <span className="gname-line">
                            <span className="gname">
                              {projectLabel(p.projectName, p.vp, p.tierCode)}
                            </span>
                            <span className="vp-code">{p.vp}</span>
                            {roleLabel && (
                              <span className="mini-pill role">{roleLabel}</span>
                            )}
                            {p.archived && (
                              <span className="mini-pill warn">Archived</span>
                            )}
                          </span>
                          <div className="stage-line">
                            <span
                              className="stage-dot"
                              style={p.archived ? { background: 'var(--rule-strong)' } : undefined}
                            />
                            {tpl.tier} ·{' '}
                            {canonicalServiceNameFor(tpl.tier) ?? tpl.displayName}
                            {vis.seesMoneyInternals && (
                              <span className="money-inline">
                                · <b>{inr(m.paidINR)}</b> paid / {inr(m.valueINR)}
                              </span>
                            )}
                          </div>
                        </span>
                        <span className="chev" aria-hidden="true">›</span>
                      </Link>
                      {/* Re-link stays an explicit, wired control below the row. */}
                      <div style={{ display: 'flex', gap: 8, margin: '6px 0 2px 58px' }}>
                        <Btn3
                          intent="ghost"
                          size="sm"
                          onClick={() =>
                            setRelinking((r) => (r === p.pid ? null : p.pid))
                          }
                        >
                          {relinking === p.pid ? 'Close re-link' : 'Re-link'}
                        </Btn3>
                        <Link to={`/conversation/${p.pid}`}>
                          <Btn3 intent="ghost" size="sm">Conversation</Btn3>
                        </Link>
                      </div>
                      {relinking === p.pid && (
                        <RelinkProjectPanel
                          project={{
                            pid: p.pid,
                            vp: p.vp,
                            projectName: p.projectName,
                            clientId: p.clientId || p.clientNo,
                            clientNo: p.clientNo,
                            clientName: displayName,
                            partnerId: p.partnerId ?? null,
                          }}
                          actorName={actorName}
                          onDone={(msg) => toast(msg)}
                          onClose={() => setRelinking(null)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <hr className="hr" />
            {/* Roles + assign-to-project (function preserved; assign lives here). */}
            <RolesSection cid={cid} liveProjects={live} actorName={actorName} />
          </div>
        )}

        {/* ---- TAB 5 · ENQUIRIES ---- */}
        {activeTab === 'enquiries' && (
          <div className="block">
            <p className="sub" style={{ margin: '0 0 var(--s-4)' }}>
              Every lead this contact raised — newest first, both open and
              converted. Each opens the enquiry in the Lead Inbox.
            </p>
            {enquiryCount === 0 ? (
              <div className="empty-state">
                <span className="empty-ico" aria-hidden="true">💬</span>
                <div className="empty-body">
                  <div className="empty-title">No enquiries on record</div>
                  <div className="empty-desc">
                    Leads raised by this contact will appear here, linked to the
                    Lead Inbox.
                  </div>
                </div>
              </div>
            ) : (
              <EnquiriesSection leadRefs={client?.leadRefs ?? []} variant="glink" />
            )}
          </div>
        )}

        {/* ---- TAB · COMMUNICATIONS (full-width Person lens, P3 Phase A) ----
             REPLACES the old cramped 320px right-side rail. Full width, one
             page scroll, reuses the rich Team-Inbox composer engine. ---- */}
        {activeTab === 'comms' && (
          <ContactCommsTab
            contactId={cid}
            contactName={headerName}
            primaryProjectId={
              visibleProjects[0]?.pid ?? projects[0]?.pid ?? undefined
            }
            projects={visibleProjects.map((p) => ({
              pid: p.pid,
              label: p.vp || p.pid,
            }))}
          />
        )}

        {/* ---- TAB 6 · ASTRO (chart + linked astro-vastu, honest states) ---- */}
        {activeTab === 'astro' && (
          <AstroPanel client={client} />
        )}
      </div>

    </div>{/* end left column */}

    </div>
  );
}

/**
 * PURE — this contact's participant role LABEL on a given project, for the
 * Projects-tab row pill. Reads `participants[]` first (the AD-009 model), then
 * the legacy `clientId`/`partnerId`. `null` → no pill (an unlabelled link).
 */
function participantRoleForContact(p: LiveProject, cid: string): string | null {
  if (p.participants && p.participants.length > 0) {
    const mine = p.participants.find((e) => e.contactId === cid);
    if (mine) {
      const MAP: Record<string, string> = {
        primary_client: 'Primary client',
        co_client: 'Co-client',
        family: 'Family member',
        consultant: 'Consultant',
        partner: 'Channel partner',
        staff: 'AV staff',
      };
      return MAP[mine.role] ?? mine.role;
    }
    return null;
  }
  if ((p.clientId || p.clientNo) === cid) return 'Primary client';
  if (p.partnerId === cid) return 'Channel partner';
  return null;
}

/* ════════════════════ Astro panel (Tab 6) — honest states ════════════════════
 *
 * Two blocks, both HONEST (no dead controls):
 *   6a · the person's INDIVIDUAL astro chart — a real View/Download link when
 *        `astroChartRef` is present, otherwise a muted "Not computed yet" state.
 *   6b · the LINKED astro-vastu charts (individual + each group) — parked: the
 *        group astro-vastu feature is not built, so the rows are non-interactive
 *        (plain divs, aria-disabled), clearly banner-marked "Coming".
 */
/**
 * LinkedAstroVastuRow — one real download row for a single Family/Organisation
 * this contact belongs to. Downloads THAT group's 23-page Astro-Vastu Report via
 * `renderAstroPdf({ groupId, kind:'astro-vastu' })`.
 *
 * Honest states (never a dead control — see [[feedback-no-dead-controls]]):
 *   · loading      — resolving the group from the engine ("Checking…").
 *   · resolved     — the group has chartable members: a LIVE download button.
 *   · unavailable  — the group has NO chartable members yet (or the engine is not
 *                    deployed): a muted, non-interactive row that states the
 *                    reason. It never no-ops silently.
 */
function LinkedAstroVastuRow({ group }: { group: FamilyGroup }) {
  const { toast } = useToast();
  const isFamily = group.type === 'family';
  const typeLabel = isFamily ? 'Family' : 'Organisation';
  const memberCount = group.members.length;

  const [phase, setPhase] = useState<'loading' | 'resolved' | 'unavailable'>('loading');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setPhase('loading');
    (async () => {
      const res = await resolveAstroGroup({ groupId: group.id });
      if (!alive) return;
      setPhase(res && res.members.length > 0 ? 'resolved' : 'unavailable');
    })();
    return () => {
      alive = false;
    };
  }, [group.id]);

  async function onDownload() {
    if (busy) return;
    setBusy(true);
    // Surface 2 — THIS group's 23-page Astro-Vastu Report.
    const url = await renderAstroPdf({ groupId: group.id, kind: 'astro-vastu' });
    setBusy(false);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast('The Astro-Vastu report is not available yet — it generates once the engine is deployed.');
    }
  }

  if (phase === 'resolved') {
    return (
      <button
        type="button"
        className="glink"
        style={{ cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
        onClick={() => void onDownload()}
        disabled={busy}
        aria-disabled={busy}
      >
        <span className={`gicon ${isFamily ? 'fam' : 'org'}`} aria-hidden="true">
          {isFamily ? '👪' : '🏢'}
        </span>
        <span className="gtext">
          <span className="gname">
            {busy ? 'Preparing…' : `${group.name} — Astro-Vastu report`}
          </span>
          <div className="grole">{typeLabel} · full Astro-Vastu Report (PDF)</div>
        </span>
        <span className="chev" aria-hidden="true">⬇</span>
      </button>
    );
  }

  const reason =
    phase === 'loading'
      ? 'Checking the engine…'
      : memberCount === 0
        ? 'No members yet — add people to this group to build its report.'
        : 'No chartable members yet — the report generates once the engine computes its members’ charts.';

  return (
    <div className="glink is-muted" aria-disabled="true">
      <span className={`gicon ${isFamily ? 'fam' : 'org'}`} aria-hidden="true">
        {isFamily ? '👪' : '🏢'}
      </span>
      <span className="gtext">
        <span className="gname">{group.name} — Astro-Vastu report</span>
        <div className="grole">{typeLabel} · {reason}</div>
      </span>
      <span className="chev" aria-hidden="true">{phase === 'loading' ? '⏳' : '○'}</span>
    </div>
  );
}

function AstroPanel({ client }: { client: LiveClient | null }) {
  const { toast } = useToast();
  const cid = client?.cid ?? '';
  const chartRef = client?.astroChartRef;

  // The individual's astro data via `resolveAstroGroup({ contactIds:[cid] })` —
  // the SAME seam the group hub uses, targeted at one person. Degrades to the
  // honest pending state when the engine is not deployed (callable → not-found).
  const [phase, setPhase] = useState<'loading' | 'resolved' | 'unavailable'>('loading');
  const [data, setData] = useState<AstroGroupResolved | null>(null);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Every Family/Organisation this contact belongs to — drives the LINKED
  // Astro-Vastu charts section below (one real download row per group).
  const [allGroups, setAllGroups] = useState<FamilyGroup[]>([]);
  useEffect(() => listenFamilies(setAllGroups), []);
  const myGroups = useMemo(
    () => (cid ? allGroups.filter((g) => g.members.some((m) => m.contactId === cid)) : []),
    [allGroups, cid],
  );

  useEffect(() => {
    let alive = true;
    if (!cid) {
      setPhase('unavailable');
      setData(null);
      return;
    }
    setPhase('loading');
    (async () => {
      const res = await resolveAstroGroup({ contactIds: [cid] });
      if (!alive) return;
      if (res && res.members.length > 0) {
        setData(res);
        setPhase('resolved');
      } else {
        setData(null);
        setPhase('unavailable');
      }
    })();
    return () => {
      alive = false;
    };
  }, [cid]);

  const me = phase === 'resolved' && data ? data.members[0] : null;

  async function onViewPdf() {
    if (pdfBusy || !cid) return;
    setPdfBusy(true);
    // Surface 1 — this person's own 2-page Bhava Chakra birth chart.
    const url = await renderAstroPdf({ contactIds: [cid], kind: 'chart' });
    setPdfBusy(false);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (chartRef) {
      // The live renderer isn't deployed yet, but a cached chart PDF exists —
      // open that rather than dead-end the control.
      window.open(chartRef, '_blank', 'noopener,noreferrer');
    } else {
      toast('The astro chart PDF is not available yet — it generates once the engine is deployed.');
    }
  }

  async function onRecalculate() {
    if (recalcBusy || !cid) return;
    setRecalcBusy(true);
    const res = await resolveAstroGroup({ contactIds: [cid] }, { recalc: true });
    if (res && res.members.length > 0) {
      setData(res);
      setPhase('resolved');
    } else {
      setPhase('unavailable');
      toast('Astro recalculation is not available yet — it goes live once the engine is deployed.');
    }
    setRecalcBusy(false);
  }

  return (
    <>
      <div className="block">
        <h2>Astro chart</h2>
        <p className="sub">
          This person’s individual astro chart is computed by the engine and stored as a
          PDF. When resolved, the ascendant and Comparative Box Matrix show below.
        </p>

        {phase === 'loading' ? (
          <div className="empty-state" role="status" aria-live="polite">
            <span className="empty-ico" aria-hidden="true">⏳</span>
            <div className="empty-body">
              <div className="empty-title">Computing…</div>
              <div className="empty-desc">Resolving this person’s astro chart from the engine.</div>
            </div>
          </div>
        ) : chartRef ? (
          <a
            href={chartRef}
            target="_blank"
            rel="noopener noreferrer"
            className="glink"
            style={{ maxWidth: 460 }}
          >
            <span className="gicon av" aria-hidden="true">🔯</span>
            <span className="gtext">
              <span className="gname">View / Download astro chart PDF</span>
              <div className="grole">Opens the computed chart in a new tab</div>
            </span>
            <span className="chev" aria-hidden="true">⬇</span>
          </a>
        ) : phase === 'resolved' ? (
          <button
            type="button"
            className="glink"
            style={{ maxWidth: 460, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
            onClick={() => void onViewPdf()}
            disabled={pdfBusy}
          >
            <span className="gicon av" aria-hidden="true">🔯</span>
            <span className="gtext">
              <span className="gname">{pdfBusy ? 'Preparing…' : 'View / Download astro chart PDF'}</span>
              <div className="grole">Generates the chart PDF from the engine</div>
            </span>
            <span className="chev" aria-hidden="true">⬇</span>
          </button>
        ) : (
          <>
            <div className="empty-state">
              <span className="empty-ico" aria-hidden="true">○</span>
              <div className="empty-body">
                <div className="empty-title">Not computed yet</div>
                <div className="empty-desc">
                  The individual astro chart PDF will appear here once the astro
                  engine is deployed and computes it.
                </div>
              </div>
            </div>
            <div className="future-caption">
              When computed, this becomes:
              <span className="willbe">⬇ View / Download astro chart PDF</span>
            </div>
          </>
        )}

        {/* Recalculate — LIVE when resolved, honest-disabled otherwise */}
        <div className="astro-actions" style={{ marginTop: 'var(--s-4)' }}>
          <button
            type="button"
            className="av-btn"
            onClick={phase === 'resolved' ? () => void onRecalculate() : undefined}
            disabled={phase !== 'resolved' || recalcBusy}
            aria-disabled={phase !== 'resolved' || recalcBusy}
            title={
              phase === 'resolved'
                ? 'Recompute this person’s chart from the engine'
                : 'Available once the Astro-Vastu engine is deployed.'
            }
          >
            {recalcBusy ? '↻ Recalculating…' : '↻ Recalculate chart'}
          </button>
          {phase !== 'resolved' && (
            <span className="av-note">
              Recalculation goes live once the Astro-Vastu engine is deployed.
            </span>
          )}
        </div>

        {/* Individual ascendant + Comparative Box Matrix — only real engine data */}
        {me && (
          <>
            <p className="av-sub" style={{ marginTop: 'var(--s-5)' }}>Chart summary</p>
            <table className="vgrid" aria-label={`${me.name} astro chart summary`}>
              <tbody>
                <tr>
                  <td className="member">Ascendant</td>
                  <td>{me.ascendantSign || '—'}</td>
                </tr>
                <tr>
                  <td className="member">Bedroom</td>
                  <td>{me.bedroomDir || '—'}</td>
                </tr>
                <tr>
                  <td className="member">Entrance</td>
                  <td>{me.entranceDir || '—'}</td>
                </tr>
                <tr>
                  <td className="member">Office</td>
                  <td>{me.officeDir || '—'}</td>
                </tr>
                <tr>
                  <td className="member">Study</td>
                  <td>{me.studyDir || '—'}</td>
                </tr>
              </tbody>
            </table>

            {me.boxMatrix.length > 0 && (
              <>
                <p className="av-sub">Comparative Box Matrix</p>
                <div className="matrix-scroll">
                  <table className="matrix" aria-label={`${me.name} box matrix`}>
                    <thead>
                      <tr className="grp">
                        <th className="boxlbl">Box (life context)</th>
                        <th>Rashi</th>
                        <th>Dir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {me.boxMatrix.map((r) => (
                        <tr key={r.box}>
                          <td className="boxlbl">
                            <b>{r.box}</b> {r.lifeContext || ''}
                          </td>
                          <td className="cell">{r.sign || '—'}</td>
                          <td className="cell">{r.direction || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <hr className="hr" />

      <div className="block">
        <h2>Linked Astro-Vastu charts</h2>
        <p className="sub">
          The Astro-Vastu Report of each Family and Organisation this person
          belongs to — one report per group, generated from its members’ charts.
          (Their own individual chart is the “Astro chart” block above.)
        </p>
        {myGroups.length === 0 ? (
          <div className="empty-state">
            <span className="empty-ico" aria-hidden="true">○</span>
            <div className="empty-body">
              <div className="empty-title">Not part of any group yet</div>
              <div className="empty-desc">
                Add this contact to a Family or Organisation (Groups tab) and its
                Astro-Vastu report will appear here.
              </div>
            </div>
          </div>
        ) : (
          <div className="grouplist">
            {myGroups.map((g) => (
              <LinkedAstroVastuRow key={g.id} group={g} />
            ))}
          </div>
        )}
        <div className="callout">
          <span className="ic" aria-hidden="true">ℹ️</span>
          <div>
            Which chart a project uses is decided by <b>project type</b>: a{' '}
            <b>residential</b> project reads the <b>Family</b>’s astro-vastu
            chart; a <b>workplace</b> project reads the <b>Organisation</b>’s.
            The person’s own chart backs both.
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-mute)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: tone || 'var(--ink)' }}>
        {value}
      </div>
    </div>
  );
}

/* ════════════════════ Enquiries (Contact spine · AD-039) ════════════════════
 *
 * The Contact→Lead history the audit found missing. A contact's
 * `client.leadRefs[]` holds every lead it is linked to (auto-matched on exact
 * phone/email, staff-confirmed, or stamped at conversion). Each ref is loaded
 * one-shot via `getLead`, then the rows are shown NEWEST FIRST — and the
 * owner-locked rule is that ALL enquiries appear, converted AND open. This is
 * the reverse direction of the lead.contactId link; no Firestore-rules change
 * (clients/ already streams leadRefs; getLead reads the leads/ doc the team
 * already reads in the Lead Inbox).
 */
export function EnquiriesSection({
  leadRefs,
  loadLead = getLead,
  variant = 'box',
}: {
  leadRefs: string[];
  /** Injectable loader (defaults to the real `getLead`); the render harness
   *  passes a seeded in-memory loader so the proof needs no Firebase. */
  loadLead?: (id: string) => Promise<Lead | null>;
  /** 'box' — the standalone Box-chromed card (dev harness / legacy callers).
   *  'glink' — bare `.glink` icon-row cards for the tabbed record's Enquiries
   *  tab (mockup fidelity). Same data + handlers either way. */
  variant?: 'box' | 'glink';
}) {
  // De-dupe ids defensively (arrayUnion should keep them unique, but a
  // backfill could double-add) before loading.
  const ids = useMemo(
    () => Array.from(new Set(leadRefs.filter(Boolean))),
    [leadRefs],
  );
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ids.length === 0) {
      setLeads([]);
      return;
    }
    let alive = true;
    setLoading(true);
    Promise.all(ids.map((id) => loadLead(id).catch(() => null)))
      .then((res) => {
        if (!alive) return;
        const ok = res.filter((l): l is Lead => l !== null);
        // Newest first (owner-locked) — by createdAt ISO, descending.
        ok.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
        setLeads(ok);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [ids, loadLead]);

  // No leadRefs at all → don't render an empty card (keeps the page clean for
  // contacts that have never been a lead, e.g. directly-created clients).
  if (ids.length === 0) return null;

  // ── Mockup-fidelity variant: bare `.glink` icon-row cards (no Box). Same
  //    loading / unavailable / loaded states, same Lead-Inbox open handler. ──
  if (variant === 'glink') {
    if (loading && leads.length === 0) {
      return <p className="note-inline">Loading enquiries…</p>;
    }
    if (leads.length === 0) {
      return (
        <EmptyNote title="No enquiries found">
          This contact has {ids.length} linked enquiry id
          {ids.length === 1 ? '' : 's'}, but the lead record
          {ids.length === 1 ? ' is' : 's are'} no longer available.
        </EmptyNote>
      );
    }
    return (
      <div className="grouplist" data-testid="enquiries-list">
        {leads.map((ld) => (
          <Link
            key={ld.id}
            to="/leads"
            className="glink"
            data-testid="enquiry-row"
            title={`Open the Lead Inbox · ${ld.id}`}
          >
            <span className="gicon" aria-hidden="true">
              {enquiryIcon(ld.source)}
            </span>
            <span className="gtext">
              <span className="gname-line">
                <span className="gname">{ld.name || ld.id}</span>
                <span className="mini-pill">
                  {LEAD_SOURCE_LABEL[ld.source] ?? ld.source}
                </span>
                <span className={`mini-pill${ld.state === 'converted' ? ' good' : ' info'}`}>
                  {LEAD_STATE_LABEL[ld.state] ?? ld.state}
                </span>
              </span>
              <div className="grole">{fmtEnquiryDate(ld.createdAt)}</div>
            </span>
            <span className="chev" aria-hidden="true">›</span>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <Box title="Enquiries" note={`${ids.length} linked`}>
      {loading && leads.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Loading enquiries…</p>
      ) : leads.length === 0 ? (
        <EmptyNote title="No enquiries found">
          This contact has {ids.length} linked enquiry id
          {ids.length === 1 ? '' : 's'}, but the lead record
          {ids.length === 1 ? ' is' : 's are'} no longer available.
        </EmptyNote>
      ) : (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          data-testid="enquiries-list"
        >
          {leads.map((ld) => (
            <Card key={ld.id} className="row-card">
              <div
                data-testid="enquiry-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <Link to="/leads" title={`Open the Lead Inbox · ${ld.id}`}>
                  <b>{ld.name || ld.id}</b>
                </Link>
                <Tag kind="tag">{LEAD_SOURCE_LABEL[ld.source] ?? ld.source}</Tag>
                <Tag kind={ld.state === 'converted' ? 'val' : 'find'}>
                  {LEAD_STATE_LABEL[ld.state] ?? ld.state}
                </Tag>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    color: 'var(--ink-mute)',
                  }}
                >
                  {fmtEnquiryDate(ld.createdAt)}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Box>
  );
}

/** Emoji chip for an enquiry's source (glink icon slot). */
function enquiryIcon(source: string): string {
  const s = source.toLowerCase();
  if (s.includes('whatsapp') || s.includes('wa')) return '💬';
  if (s.includes('web') || s.includes('form') || s.includes('site')) return '🌐';
  if (s.includes('tool') || s.includes('astro') || s.includes('chart')) return '🌿';
  return '💬';
}

/** Short, locale-safe enquiry date. Falls back to the raw ISO if unparseable. */
function fmtEnquiryDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso || '—';
  return new Date(t).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/* ════════════════════ Send access to client ════════════════════
 *
 * 2026-06-15 (owner-confirmed, CORRECTED) — a staff member sends the client a
 * sign-in LINK over BOTH email (Brevo) and WhatsApp (AiSensy) in one press.
 * The link opens the UNIFIED /login (Google-primary). This is a LINK send — it
 * must NOT fire a 4-digit OTP code. The code stays only as the client-chosen
 * fallback ON /login ("Use a code instead") for clients with no Google account.
 *
 * Vehicle: the `sendClientPortalLink` callable (avbp_client_link.js). It
 * resolves the client by handle and emails + WhatsApps the /login URL plus the
 * "sign in with the same email you gave Aayatan Veda" note. No OTP is sent.
 */
function SendClientAccess({
  cid,
  client,
  fallbackNo,
}: {
  cid: string;
  client: LiveClient | null;
  fallbackNo: string;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  // No master record → no canonical email/phone to deliver to; the OTP send
  // would have nothing on file. Hide the control rather than fire a doomed
  // send (the contact lives on the project until a client master exists).
  if (!client) return null;

  const target = {
    clientNo: client.clientNo || fallbackNo || cid,
    email: client.email,
    phone: client.phone,
  };
  const channels = hasDeliveryChannel(target);

  async function send() {
    if (busy) return;
    const handle = pickAccessHandle(target);
    if (!handle) {
      toast('No client number / email / phone to send to.');
      return;
    }
    setBusy(true);
    try {
      const fn = httpsCallable<{ handle: string }, SendLinkResp>(
        functions,
        'sendClientPortalLink',
      );
      const res = await fn({ handle });
      const outcome = interpretSendAccess(res.data);
      toast(outcome.message, { ok: outcome.ok });
    } catch (e) {
      toast(`Could not send sign-in: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Lbl>Contact sign-in</Lbl>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn3
          intent="ink"
          size="sm"
          onClick={send}
          disabled={busy || !channels}
          title={
            channels
              ? undefined
              : 'Add an email or phone to this client first.'
          }
        >
          {busy ? 'Sending…' : 'Send access to client'}
        </Btn3>
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {channels
            ? 'Emails + WhatsApps a sign-in LINK to /login (not a code). They sign in with Google — using the same email you have on file — or pick a code if they have no Gmail.'
            : 'Add an email or phone above to enable sending.'}
        </span>
      </div>
    </>
  );
}

/* ════════════════════ Send intake form (M5-gaps ITEM 1) ════════════════════
 *
 * A staff trigger that mints + sends an intake-form link for THIS contact,
 * scoped to one of their projects. Intake links are project-scoped (pid is
 * how the engagement/tier + property attach), so:
 *   · 0 projects → there is nothing to scope the link to → guidance toast.
 *   · 1 project  → open IntakeLinkSendBar directly with {cid, pid}.
 *   · many       → a project picker first, then the SendBar with the chosen pid.
 * IntakeLinkSendBar carries its own service selector (L1A default) + smart
 * WA/email channel detection — same component ProjectDetail/PropertyDetail use.
 */
function SendIntakeForm({
  cid,
  projects,
}: {
  cid: string;
  projects: LiveProject[];
}) {
  const { toast } = useToast();
  // null = closed; otherwise the pid the SendBar is scoped to.
  const [sendPid, setSendPid] = useState<string | null>(null);
  // The inline project picker (only for the multi-project case).
  const [picking, setPicking] = useState(false);

  function onClick() {
    if (projects.length === 0) {
      toast('No project for this contact yet. Create a project first.');
      return;
    }
    if (projects.length === 1) {
      setSendPid(projects[0].pid);
      return;
    }
    setPicking((p) => !p);
  }

  return (
    <>
      <Lbl>Send intake form</Lbl>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn3 intent="ink" size="sm" onClick={onClick}>
          {picking ? 'Close' : 'Send Intake Form'}
        </Btn3>
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {projects.length === 0
            ? 'Create a project for this contact first to send an intake form.'
            : 'Mints + sends the client intake form (WhatsApp + email) for one of this contact’s projects.'}
        </span>
      </div>

      {/* Multi-project: pick which project the link is scoped to. */}
      {picking && projects.length > 1 && (
        <Card flat style={{ marginTop: 10 }}>
          <Lbl>Which project?</Lbl>
          <select
            defaultValue=""
            onChange={(e) => {
              const pid = e.target.value;
              if (!pid) return;
              setPicking(false);
              setSendPid(pid);
            }}
            aria-label="Choose the project for the intake form"
            style={{ ...inputStyle, maxWidth: 420 }}
          >
            <option value="">— Select a project —</option>
            {projects.map((p) => (
              <option key={p.pid} value={p.pid}>
                {projectLabel(p.projectName, p.vp, p.tierCode)} ({p.vp})
              </option>
            ))}
          </select>
        </Card>
      )}

      {sendPid && (
        <IntakeLinkSendBar
          cid={cid}
          pid={sendPid}
          onClose={() => setSendPid(null)}
        />
      )}
    </>
  );
}

/* ════════════════════ Client contact (view + edit) ════════════════════ */

function ClientContact({
  cid,
  client,
  fallbackName,
  actorName,
  onSaved,
  editing: editingProp,
  onEditingChange,
}: {
  cid: string;
  client: LiveClient | null;
  fallbackName: string;
  actorName: string;
  onSaved: (c: LiveClient, msg: string) => void;
  /** Controlled edit-mode (the header "Edit details" button drives it). When
   *  omitted the component owns its own edit state (its inline "Edit" button). */
  editing?: boolean;
  onEditingChange?: (v: boolean) => void;
}) {
  const [editingInternal, setEditingInternal] = useState(false);
  const editing = editingProp ?? editingInternal;
  const setEditing = (v: boolean) => {
    setEditingInternal(v);
    onEditingChange?.(v);
  };
  const [name, setName] = useState(client?.name || fallbackName);
  const [phone, setPhone] = useState(client?.phone || '');
  const [email, setEmail] = useState(client?.email || '');
  const [location, setLocation] = useState(client?.location || '');
  const [busy, setBusy] = useState(false);

  // Phones array editor — seeded from migrated phones on mount.
  const initPhones = (): PhoneEntry[] => {
    if (!client) return [];
    const { phones } = migratePhoneField({ phone: client.phone, phones: client.phones ?? [] });
    return phones.length > 0 ? phones : [];
  };
  const [phonesEdit, setPhonesEdit] = useState<PhoneEntry[]>(initPhones);

  // Emails array editor (P1 §6 / datamap #4a) — the model always had emails[]
  // but no editor. Seeded via the same migration that folds the legacy single
  // `email` string into the array form.
  const initEmails = (): EmailEntry[] => {
    if (!client) return [];
    const { emails } = migratePhoneField({ email: client.email, emails: client.emails ?? [] });
    return emails;
  };
  const [emailsEdit, setEmailsEdit] = useState<EmailEntry[]>(initEmails);

  // Contact-side role assignment (P1 §6 / datamap #14) — the controlled
  // roleTags[] set. Seeded from the contact; "Client" is never here (derived).
  const [roleTagsEdit, setRoleTagsEdit] = useState<string[]>(client?.roleTags ?? []);

  useEffect(() => {
    if (client) {
      setName(client.name || fallbackName);
      setPhone(client.phone);
      setEmail(client.email);
      setLocation(client.location);
      const { phones, emails } = migratePhoneField({
        phone: client.phone,
        phones: client.phones ?? [],
        email: client.email,
        emails: client.emails ?? [],
      });
      setPhonesEdit(phones.length > 0 ? phones : []);
      setEmailsEdit(emails);
      setRoleTagsEdit(client.roleTags ?? []);
    }
  }, [client, fallbackName]);

  async function save() {
    if (busy || !client) return;
    setBusy(true);
    try {
      // The phones array editor is the authoritative source; if it has entries
      // keep it. Otherwise fall back to migrating the legacy `phone` field.
      const phonesToSave: PhoneEntry[] =
        phonesEdit.length > 0
          ? phonesEdit
          : migratePhoneField({ phone: phone.trim() || client.phone, phones: [] }).phones;
      // The emails array editor is authoritative — drop blank rows. The legacy
      // flat `email` mirrors the primary (first) address for back-compat readers.
      const emailsToSave: EmailEntry[] = emailsEdit
        .map((e) => ({ address: e.address.trim(), label: e.label?.trim() || undefined }))
        .filter((e) => e.address);
      const legacyEmail = emailsToSave[0]?.address ?? email.trim();
      // Controlled role tags — drop blanks / de-dupe.
      const roleTagsToSave = [...new Set(roleTagsEdit.filter(Boolean))];
      await updateClient(
        client.clientNo,
        {
          name: name.trim(),
          phone: phone.trim(),
          email: legacyEmail,
          location: location.trim(),
        },
        actorName,
      );
      // Write phones[] / emails[] / roleTags[] separately (ClientEditPatch is the
      // legacy-shape only).
      if (phonesToSave.length > 0) {
        await savePhones(client.cid, phonesToSave);
      }
      await saveEmails(client.cid, emailsToSave);
      await saveRoleTags(client.cid, roleTagsToSave);
      onSaved(
        {
          ...client,
          name: name.trim(),
          phone: phone.trim(),
          email: legacyEmail,
          location: location.trim(),
          phones: phonesToSave.length > 0 ? phonesToSave : client.phones,
          emails: emailsToSave,
          roleTags: roleTagsToSave,
        },
        `Saved client ${client.clientNo}.`,
      );
      setEditing(false);
    } catch (e) {
      onSaved(client, `Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!client) {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
        No separate client master record for {cid} — contact details live on
        the project. Convert a lead or use "+ New client" to create one.
      </p>
    );
  }

  if (!editing) {
    // Read-mode — mockup-style layout (fields grid + phone/email rows). Reuses
    // the SAME data the editor writes (phones[] / emails[] / roleTags[]).
    const { phones: readPhones } = migratePhoneField({
      phone: client.phone,
      phones: client.phones ?? [],
    });
    const allEmails =
      client.emails && client.emails.length > 0
        ? client.emails
        : client.email
          ? [{ address: client.email }]
          : [];
    const assignedRoleLabels = (client.roleTags ?? [])
      .map((t) => ASSIGNABLE_CONTACT_ROLES.find((r) => r.value === t)?.label ?? t)
      .filter(Boolean);
    const structuredAddr = client.address
      ? [
          client.address.street,
          client.address.city,
          client.address.state,
          client.address.pin,
          client.address.country,
        ]
          .filter(Boolean)
          .join(', ')
      : '';
    const addressLine = structuredAddr || client.location || '';

    return (
      <>
        <div className="block">
          <h2>Contact details</h2>
          <div className="fields">
            <div className="f">
              <label>Full name</label>
              <div className="val">{client.name || fallbackName || '—'}</div>
            </div>
            <div className="f">
              <label>Referred by</label>
              <div className="val">{client.referredBy?.name || '—'}</div>
            </div>
            <div className="f span2">
              <label>Address</label>
              <div className="val wide">{addressLine || '—'}</div>
            </div>
            {assignedRoleLabels.length > 0 && (
              <div className="f span2">
                <label>Directory roles</label>
                <div className="val wide">{assignedRoleLabels.join(', ')}</div>
              </div>
            )}
          </div>
        </div>

        <hr className="hr" />

        <div className="block">
          <h2>Phone numbers</h2>
          {readPhones.length === 0 ? (
            <p className="note-inline">No phone number on record.</p>
          ) : (
            <div className="rows">
              {readPhones.map((ph, i) => (
                <div className="datarow" key={`${ph.number}-${i}`}>
                  <span className="primary">{ph.number || '—'}</span>
                  <span className="spacer" />
                  {ph.whatsApp && <span className="rtag wa">WhatsApp</span>}
                  {ph.label && <span className="rtag">{ph.label}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <hr className="hr" />

        <div className="block">
          <h2>Email addresses</h2>
          {allEmails.length === 0 ? (
            <p className="note-inline">No email address on record.</p>
          ) : (
            <div className="rows">
              {allEmails.map((em, i) => (
                <div className="datarow" key={`${em.address}-${i}`}>
                  <span className="primary">{em.address}</span>
                  <span className="spacer" />
                  {em.label && <span className="rtag">{em.label}</span>}
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <Btn3 intent="ghost" size="sm" onClick={() => setEditing(true)}>
              Edit contact details
            </Btn3>
          </div>
        </div>
      </>
    );
  }

  /** Toggle whatsApp flag — radio-group: checking one auto-unchecks the rest */
  function setWhatsApp(idx: number, checked: boolean) {
    setPhonesEdit((prev) =>
      prev.map((p, i) =>
        checked
          ? { ...p, whatsApp: i === idx ? true : undefined }
          : i === idx
            ? { ...p, whatsApp: false }
            : p,
      ),
    );
  }

  function updatePhoneNumber(idx: number, value: string) {
    setPhonesEdit((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, number: value } : p)),
    );
  }

  function addPhoneRow() {
    setPhonesEdit((prev) => [...prev, { number: '', label: '' }]);
  }

  function removePhoneRow(idx: number) {
    setPhonesEdit((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Emails multi-editor handlers (P1 §6 / datamap #4a) ──
  function updateEmailAddress(idx: number, value: string) {
    setEmailsEdit((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, address: value } : e)),
    );
  }
  function updateEmailLabel(idx: number, value: string) {
    setEmailsEdit((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, label: value } : e)),
    );
  }
  function addEmailRow() {
    setEmailsEdit((prev) => [...prev, { address: '', label: '' }]);
  }
  function removeEmailRow(idx: number) {
    setEmailsEdit((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Contact-side role toggle (P1 §6 / datamap #14) ──
  function toggleRole(value: string, checked: boolean) {
    setRoleTagsEdit((prev) =>
      checked ? [...new Set([...prev, value])] : prev.filter((r) => r !== value),
    );
  }

  return (
    <div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Client name"
        style={inputStyle}
      />
      {/* ── Multi-phone editor with WhatsApp flag ── */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginBottom: 4 }}>
          Phone numbers
        </div>
        {phonesEdit.map((entry, idx) => (
          <div
            key={idx}
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}
          >
            <input
              value={entry.number}
              onChange={(e) => updatePhoneNumber(idx, e.target.value)}
              placeholder="Phone number"
              style={{ ...inputStyle, flex: 1, minWidth: 140, marginTop: 0 }}
              data-testid={`phone-number-${idx}`}
            />
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer' }}
              title="This number is used for WhatsApp"
            >
              <input
                type="checkbox"
                checked={!!entry.whatsApp}
                onChange={(e) => setWhatsApp(idx, e.target.checked)}
                data-testid={`phone-wa-${idx}`}
              />
              WhatsApp
            </label>
            <button
              type="button"
              onClick={() => removePhoneRow(idx)}
              style={{ fontSize: 12, color: 'var(--ink-mute)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
              aria-label="Remove phone"
            >
              ×
            </button>
          </div>
        ))}
        <Btn3 intent="ghost" size="sm" onClick={addPhoneRow} className="">
          + Add phone
        </Btn3>
      </div>
      {/* ── Multi-email editor (P1 §6 / datamap #4a) ── */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginBottom: 4 }}>
          Email addresses
        </div>
        {emailsEdit.map((entry, idx) => (
          <div
            key={idx}
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}
          >
            <input
              type="email"
              value={entry.address}
              onChange={(e) => updateEmailAddress(idx, e.target.value)}
              placeholder="name@example.com"
              style={{ ...inputStyle, flex: 2, minWidth: 160, marginTop: 0 }}
              data-testid={`email-address-${idx}`}
            />
            <input
              value={entry.label ?? ''}
              onChange={(e) => updateEmailLabel(idx, e.target.value)}
              placeholder="Label (Work / Personal)"
              style={{ ...inputStyle, flex: 1, minWidth: 120, marginTop: 0 }}
              data-testid={`email-label-${idx}`}
            />
            <button
              type="button"
              onClick={() => removeEmailRow(idx)}
              style={{ fontSize: 12, color: 'var(--ink-mute)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
              aria-label="Remove email"
            >
              ×
            </button>
          </div>
        ))}
        <Btn3 intent="ghost" size="sm" onClick={addEmailRow} className="">
          + Add email
        </Btn3>
      </div>
      <div style={{ marginTop: 10 }}>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Location"
          style={{ ...inputStyle, width: '100%' }}
        />
      </div>
      {/* ── Contact-side role assignment (P1 §6 / datamap #14) ── */}
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginBottom: 6 }}>
          Roles &mdash; where this contact appears in the directory
          <span style={{ color: 'var(--ink-mute)' }}>
            {' '}(Client is set automatically when a project is linked)
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {ASSIGNABLE_CONTACT_ROLES.map((r) => (
            <label
              key={r.value}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={roleTagsEdit.includes(r.value)}
                onChange={(e) => toggleRole(r.value, e.target.checked)}
                data-testid={`role-${r.value}`}
              />
              {r.label}
            </label>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Btn3 intent="grn" size="sm" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save contact'}
        </Btn3>
        <Btn3
          intent="ghost"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={busy}
        >
          Cancel
        </Btn3>
      </div>
    </div>
  );
}

/* ════════════════════ Manage client — archive / Trash / Merge ════════════════════
 *
 * 2026-05-22 — the client's OWN lifecycle controls (Recoverable Trash batch).
 *  · Archive  — the client's own archive flag (ARCHIVE-FIX: archiving a
 *               project no longer flips the client master).
 *  · Trash    — move the client to the recoverable Trash. RELATIONSHIP
 *               RULE: blocked while the client still owns active projects
 *               (the guard surfaces the reason and points to Merge).
 *  · Merge    — merge a DUPLICATE: re-point every project of a duplicate
 *               onto a survivor, carry over missing contact fields, trash
 *               the now-empty duplicate. This Client Detail is treated as
 *               the DUPLICATE; the picker chooses the SURVIVOR.
 */
function ManageClient({
  client,
  liveProjects,
  masters,
  mayTrash,
  mayMerge,
  actorName,
  onChanged,
}: {
  client: LiveClient;
  liveProjects: LiveProject[];
  masters: LiveClient[];
  mayTrash: boolean;
  mayMerge: boolean;
  actorName: string;
  onChanged: (updated: LiveClient, msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'none' | 'merge'>('none');
  const [survivor, setSurvivor] = useState<{
    clientId: string;
    clientNo: string;
    name: string;
  } | null>(null);

  // RELATIONSHIP RULE — can this client be trashed right now?
  const trashBlock = clientTrashBlock(liveProjects, client.cid);

  async function doArchive() {
    if (busy) return;
    setBusy(true);
    try {
      await archiveClient(client.clientNo, actorName, !client.archived);
      onChanged(
        { ...client, archived: !client.archived },
        `Client ${client.archived ? 'un-archived' : 'archived'}.`,
      );
    } catch (e) {
      onChanged(client, `Archive failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doTrash() {
    if (busy) return;
    if (client.trashed) {
      // Restore.
      setBusy(true);
      try {
        await trashClient(client.clientNo, actorName, false);
        onChanged(
          { ...client, trashed: false },
          'Client restored from Trash.',
        );
      } catch (e) {
        onChanged(client, `Restore failed: ${(e as Error).message}`);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (trashBlock) {
      onChanged(client, trashBlock);
      return;
    }
    if (
      !window.confirm(
        'Move this client to the recoverable Trash? It is removed from ' +
          'every list but fully recoverable from the Trash screen.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await trashClient(client.clientNo, actorName, true, liveProjects);
      onChanged(
        { ...client, trashed: true },
        'Client moved to Trash (recoverable).',
      );
    } catch (e) {
      onChanged(client, `Trash failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doMerge() {
    if (busy || !survivor) return;
    if (survivor.clientId === client.cid) {
      onChanged(client, 'Pick a DIFFERENT client to merge into.');
      return;
    }
    const survivorMaster = masters.find((m) => m.cid === survivor.clientId);
    if (!survivorMaster) {
      onChanged(client, 'The chosen survivor client could not be resolved.');
      return;
    }
    const dupProjects = liveProjects.filter(
      (p) => (p.clientId || p.clientNo) === client.cid,
    ).length;
    if (
      !window.confirm(
        `Merge this client (${client.name}) INTO ${survivorMaster.name} ` +
          `(${survivorMaster.clientNo})?\n\n` +
          `${dupProjects} project${dupProjects === 1 ? '' : 's'} will be ` +
          `re-pointed onto ${survivorMaster.clientNo}, and this client ` +
          `record will be moved to Trash.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await mergeClients(
        survivorMaster,
        client,
        actorName,
        liveProjects,
      );
      onChanged(
        { ...client, trashed: true },
        `Merged into ${survivorMaster.clientNo} — ${res.projectsMoved} ` +
          `project${res.projectsMoved === 1 ? '' : 's'} re-pointed` +
          `${res.contactCarried ? ', contact details carried over' : ''}. ` +
          `This duplicate is now in Trash.`,
      );
      setMode('none');
      setSurvivor(null);
    } catch (e) {
      onChanged(client, `Merge failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Lbl>Manage contact</Lbl>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn3 intent="ghost" size="sm" onClick={doArchive} disabled={busy}>
          {client.archived ? 'Un-archive client' : 'Archive client'}
        </Btn3>
        {mayTrash && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={doTrash}
            disabled={busy}
            title={
              !client.trashed && trashBlock ? trashBlock : undefined
            }
          >
            {client.trashed ? 'Restore from Trash' : 'Move to Trash…'}
          </Btn3>
        )}
        {mayMerge && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setMode((m) => (m === 'merge' ? 'none' : 'merge'))}
            disabled={busy}
          >
            {mode === 'merge' ? 'Cancel merge' : 'Merge into…'}
          </Btn3>
        )}
      </div>

      {/* Relationship-rule notice — why Trash is blocked. */}
      {mayTrash && !client.trashed && trashBlock && (
        <p
          style={{
            fontSize: 12,
            color: 'var(--ink-soft)',
            margin: '8px 0 0',
            borderLeft: '3px solid var(--accent)',
            paddingLeft: 10,
          }}
        >
          {trashBlock}
        </p>
      )}

      {/* Merge — pick the SURVIVING client. */}
      {mode === 'merge' && (
        <Card flat style={{ marginTop: 10 }}>
          <Lbl>Merge this client INTO…</Lbl>
          <p
            style={{
              fontSize: 12,
              color: 'var(--ink-mute)',
              margin: '0 0 8px',
            }}
          >
            Pick the client that should SURVIVE. Every project of{' '}
            <b>{client.name}</b> ({client.clientNo}) will be re-pointed onto
            it; this record is then moved to Trash.
          </p>
          <ClientPicker
            value={survivor?.clientId || ''}
            onPick={(c) =>
              setSurvivor({
                clientId: c.clientId,
                clientNo: c.clientNo,
                name: c.name,
              })
            }
            ariaLabel="Search the surviving client"
            placeholder="Type the surviving client's name or number…"
          />
          {survivor && (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--ink-soft)',
                margin: '8px 0',
              }}
            >
              Survivor:{' '}
              <b style={{ color: 'var(--ink)' }}>{survivor.name}</b> (
              {survivor.clientNo})
            </div>
          )}
          <Btn3
            intent="grn"
            size="sm"
            onClick={doMerge}
            disabled={busy || !survivor}
          >
            {busy ? 'Merging…' : 'Merge clients'}
          </Btn3>
        </Card>
      )}
    </>
  );
}

/* ════════════════════ + New project for this client ════════════════════ */

function NewProjectForm({
  client,
  actorName,
  onDone,
}: {
  client: { clientId: string; clientNo: string; name: string };
  actorName: string;
  onDone: (msg: string) => void;
}) {
  const [tier, setTier] = useState<CoreTier>(CORE_TIERS[0]);
  const [sqft, setSqft] = useState('');
  // The project's OWN name (2026-05-22) — its identity, distinct from the
  // client and the VP code. Blank → the data layer writes the
  // `<tier> — <client name>` default so the project is never nameless.
  const [projectName, setProjectName] = useState('');
  // PARTNER SPRINT 2026-05-21 — a project may OPTIONALLY carry a channel
  // partner (spec §3). '' = no partner → a direct AV engagement.
  const [partnerId, setPartnerId] = useState('');
  const [partners, setPartners] = useState<LivePartner[]>([]);
  const [busy, setBusy] = useState(false);

  // The partner directory for the optional picker (live, no extra cost —
  // a partner master is a tiny doc; the picker shows only un-archived).
  useEffect(() => {
    const unsub = listenPartners(
      (rows) => setPartners(rows.filter((p) => !p.archived)),
      () => {
        /* the picker just shows no partners if this fails */
      },
    );
    return unsub;
  }, []);

  async function create() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await createProjectForClient(
        client,
        tier,
        sqft.trim() ? Number(sqft) : null,
        actorName,
        partnerId || null,
        projectName.trim(),
      );
      const partnerName =
        partnerId &&
        partners.find((p) => p.pid === partnerId)?.name;
      onDone(
        `Created project ${res.projectId} for ${client.clientNo} ` +
          `(${client.name})` +
          (partnerName ? ` · partner ${partnerName}` : '') +
          `.`,
      );
    } catch (e) {
      onDone(`Create failed: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  return (
    <Card flat style={{ marginBottom: 14 }}>
      <Lbl>New project — {client.name} ({client.clientNo})</Lbl>
      {/* The project's OWN name — name it at creation so two same-tier
          projects for this client (e.g. residence vs office) are told
          apart. Blank → a `<tier> — <client name>` default is written. */}
      <input
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        placeholder={`Project name — e.g. "${tier} — ${client.name}"`}
        aria-label="Project name"
        style={{ ...inputStyle, marginBottom: 8 }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value as CoreTier)}
          style={{ ...inputStyle, minWidth: 260, flex: '1 1 260px' }}
        >
          {canonicalTierOptions().map((o) => (
            <option key={o.tier} value={o.tier}>
              {o.tier} · {o.displayName}
            </option>
          ))}
        </select>
        <input
          value={sqft}
          onChange={(e) => setSqft(e.target.value.replace(/[^\d]/g, ''))}
          placeholder="Sqft (computed tiers)"
          style={{ ...inputStyle, width: 180 }}
        />
      </div>
      {/* PARTNER SPRINT — optional channel-partner picker. */}
      <div style={{ marginTop: 8 }}>
        <select
          value={partnerId}
          onChange={(e) => setPartnerId(e.target.value)}
          aria-label="Channel partner (optional)"
          style={{ ...inputStyle, minWidth: 260 }}
        >
          <option value="">No channel partner (direct AV engagement)</option>
          {partners.map((p) => (
            <option key={p.pid} value={p.pid}>
              {p.name || p.partnerNo} ({p.partnerNo})
            </option>
          ))}
        </select>
      </div>
      <p
        style={{
          fontSize: 11.5,
          color: 'var(--ink-mute)',
          margin: '8px 0 0',
        }}
      >
        A fresh VP project number is minted; the existing client number
        ({client.clientNo}) is kept. Give the project its own name so it is
        easy to tell apart from the client's other projects — leave it blank
        and a <i>tier — client name</i> default is used. Pricing is the
        locked auto-quote. A channel partner is optional.
      </p>
      <div style={{ marginTop: 12 }}>
        <Btn3 intent="grn" size="sm" onClick={create} disabled={busy}>
          {busy ? 'Creating…' : 'Create project'}
        </Btn3>
      </div>
    </Card>
  );
}

/* ════════════════════ Personal data card (Module 2 / AD-008) ════════════════════
 *
 * DOB / TOB (24h, no DST — AV policy) / POB / Prakriti — captured once
 * per contact, shared by Astro engine + reports. TOB is plain HH:MM text;
 * no timezone correction is applied (AV no-DST policy is locked).
 */

// Prakriti — 12 canonical codes (owner-locked 2026-06-17)
// Format: CODE — description shown in the dropdown option
const PRAKRITI_OPTIONS = [
  // Category A — Single dominant
  { code: 'V/PK', desc: 'Vaat dominant; Pitta-Kapha balanced secondary' },
  { code: 'V/P',  desc: 'Vaat dominant; Pitta second; Kapha negligible' },
  { code: 'V/K',  desc: 'Vaat dominant; Kapha second; Pitta negligible' },
  { code: 'P/VK', desc: 'Pitta dominant; Vaat-Kapha balanced secondary' },
  { code: 'P/V',  desc: 'Pitta dominant; Vaat second; Kapha negligible' },
  { code: 'P/K',  desc: 'Pitta dominant; Kapha second; Vaat negligible' },
  { code: 'K/VP', desc: 'Kapha dominant; Vaat-Pitta balanced secondary' },
  { code: 'K/V',  desc: 'Kapha dominant; Vaat second; Pitta negligible' },
  { code: 'K/P',  desc: 'Kapha dominant; Pitta second; Vaat negligible' },
  // Category B — Co-dominant
  { code: 'VP/K', desc: 'Vaat-Pitta co-dominant; Kapha negligible' },
  { code: 'PK/V', desc: 'Pitta-Kapha co-dominant; Vaat negligible' },
  { code: 'VK/P', desc: 'Vaat-Kapha co-dominant; Pitta negligible' },
] as const;

/**
 * Read the STRUCTURED city/state/country off a Contact's pob (owner fix
 * 2026-07-02). These are the source of truth — we NEVER split `pob.name` back
 * into city/state/country (that lossy round-trip mislabelled "Delhi, India" as
 * state="India"). LEGACY fallback ONLY: an old doc that carries just `pob.name`
 * and no structured fields still parses the display string so nothing regresses
 * to blank until it is re-saved / re-migrated into the structured shape.
 */
function readPobParts(pob?: PlaceOfBirth): [string, string, string] {
  const city = (pob?.city ?? '').trim();
  const state = (pob?.state ?? '').trim();
  const country = (pob?.country ?? '').trim();
  if (city || state || country) return [city, state, country];
  // Legacy-only: no structured fields present — parse the display name once.
  const parts = (pob?.name ?? '').split(',').map((s) => s.trim());
  return [parts[0] ?? '', parts[1] ?? '', parts[2] ?? ''];
}

/** Format a decimal coordinate to ≤6 dp, dropping trailing zeros. */
function trimDp(n: number): string {
  return String(Number(n.toFixed(6)));
}

function PersonalDataCard({
  cid,
  client,
  liveContacts,
  onSaved,
}: {
  cid: string;
  client: LiveClient | null;
  liveContacts: LiveClient[];
  onSaved: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [dob, setDob] = useState(client?.dob ?? '');
  // TOB stored as tight "HH:MM"; normalize the seed so an already-stored value
  // renders identically through the same mask the field types into.
  const [tob, setTob] = useState(() => maskTob(client?.tob ?? ''));

  // POB — read STRUCTURED city / state / country (never parsed from pob.name)
  const initPob = (): [string, string, string] => readPobParts(client?.pob);
  const [pobCity, setPobCity] = useState(() => initPob()[0]);
  const [pobState, setPobState] = useState(() => initPob()[1]);
  const [pobCountry, setPobCountry] = useState(() => initPob()[2]);

  // POB coordinates — visible/confirmable field mirroring the Intake People
  // module (IntakeModule.tsx §4069-4217). Seeded from the stored pob.lat/lon
  // (decimal, `lon` key, already the correct engine shape). A real 0/0 is a
  // legacy placeholder, never a place — so we treat it as "empty" for display.
  const seedCoords = (pob?: PlaceOfBirth): string => {
    const lat = pob?.lat;
    const lon = pob?.lon;
    if (typeof lat === 'number' && typeof lon === 'number' && (lat !== 0 || lon !== 0)) {
      return `${trimDp(lat)}, ${trimDp(lon)}`;
    }
    return '';
  };
  const [pobCoords, setPobCoords] = useState<string>(() => seedCoords(client?.pob));
  const [geoBusy, setGeoBusy] = useState(false);
  const [coordWarn, setCoordWarn] = useState('');

  // Prakriti — 12-type grouped select; '' = not set
  const [prakriti, setPrakriti] = useState<string>(client?.prakriti ?? '');

  // Per-person Health (astro-subject step ③) — free-text context, portal-side PII.
  const [health, setHealth] = useState<string>(client?.health ?? '');

  // Fix 2 — Address fields
  const [addrStreet, setAddrStreet] = useState(client?.address?.street ?? '');
  const [addrCity, setAddrCity] = useState(client?.address?.city ?? '');
  const [addrState, setAddrState] = useState(client?.address?.state ?? '');
  const [addrPin, setAddrPin] = useState(client?.address?.pin ?? '');
  const [addrCountry, setAddrCountry] = useState(client?.address?.country ?? '');
  const [copyStatus, setCopyStatus] = useState('');

  // Fix 3 — Referred by typeahead
  const [refSearch, setRefSearch] = useState(
    client?.referredBy ? `${client.referredBy.name} · ${client.referredBy.contactId}` : '',
  );
  const [referredBy, setReferredBy] = useState<{ contactId: string; name: string } | undefined>(
    client?.referredBy,
  );
  const [refDropdown, setRefDropdown] = useState(false);

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (client) {
      setDob(client.dob ?? '');
      setTob(maskTob(client.tob ?? ''));
      const [city, state, country] = readPobParts(client.pob);
      setPobCity(city);
      setPobState(state);
      setPobCountry(country);
      setPobCoords(seedCoords(client.pob));
      setCoordWarn('');
      setPrakriti(client.prakriti ?? '');
      setHealth(client.health ?? '');
      setAddrStreet(client.address?.street ?? '');
      setAddrCity(client.address?.city ?? '');
      setAddrState(client.address?.state ?? '');
      setAddrPin(client.address?.pin ?? '');
      setAddrCountry(client.address?.country ?? '');
      if (client.referredBy) {
        setReferredBy(client.referredBy);
        setRefSearch(`${client.referredBy.name} · ${client.referredBy.contactId}`);
      } else {
        setReferredBy(undefined);
        setRefSearch('');
      }
    }
  }, [client]);

  // Fix 3 — contacts matching the ref typeahead query
  const refMatches = (() => {
    const q = refSearch.trim().toLowerCase();
    if (!q || referredBy) return [];
    return liveContacts
      .filter((c) => c.cid !== cid && !c.trashed)
      .filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.clientNo.toLowerCase().includes(q),
      )
      .slice(0, 5);
  })();

  // Fix 2 — copy address to clipboard
  async function copyAddress() {
    const parts = [
      client?.name ?? '',
      addrStreet.trim(),
      [addrCity.trim(), addrPin.trim() ? `- ${addrPin.trim()}` : ''].filter(Boolean).join(' '),
      [addrState.trim(), addrCountry.trim()].filter(Boolean).join(', '),
    ].filter(Boolean);
    if (parts.length === 0) return;
    try {
      await navigator.clipboard.writeText(parts.join('\n'));
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch {
      setCopyStatus('Copy failed');
    }
  }

  // Auto-fill POB coordinates from City + State + Country on blur of the place
  // group — reuses the SAME Nominatim geocoder as the Intake People module
  // (IntakeModule.tsx §4074-4094, `autoGeocodePob`). No new geocode service.
  // Only fills when the Coordinates box is still empty; a manual paste wins.
  async function autoGeocodePob() {
    const city = pobCity.trim();
    if (!city) return;
    if (pobCoords.trim()) return; // don't clobber an entered coordinate
    setGeoBusy(true);
    try {
      const country = pobCountry.trim() || 'India';
      const r = await geocode({
        place: city,
        state: pobState.trim() || undefined,
        country,
        country_code: countryCodeHint(country),
      });
      if (r && !pobCoords.trim()) {
        setPobCoords(`${trimDp(r.lat)}, ${trimDp(r.lon)}`);
        setCoordWarn('');
      }
    } finally {
      setGeoBusy(false);
    }
  }

  // City picker SELECT — auto-fill City + State + Country + Coordinates in one
  // action from the chosen Nominatim candidate (structured city/state/country
  // via addressdetails). Overwrites the coordinate box because the user
  // explicitly disambiguated the place; manual typing (below) still falls back
  // to the blur-geocode.
  function onCityPicked(hit: CitySearchHit) {
    setPobCity(hit.city || pobCity);
    setPobState(hit.state || '');
    setPobCountry(hit.country || '');
    setPobCoords(`${trimDp(hit.lat)}, ${trimDp(hit.lon)}`);
    setCoordWarn('');
  }

  // TOB validity — empty is allowed (clears the field); a partial/out-of-range
  // value blocks save and shows an inline hint. Never store a malformed time.
  const tobInvalid = tob.trim() !== '' && !isCompleteTob(tob);

  async function save() {
    if (busy || !client) return;
    if (tobInvalid) {
      onSaved('Time of birth must be HH:MM, 24-hour (00–23 : 00–59).');
      return;
    }
    setBusy(true);
    try {
      // POB — STRUCTURED city/state/country are the source of truth (owner fix
      // 2026-07-02); `name` is regenerated as a display-only join, NEVER parsed
      // back out. `state` stays exactly what staff typed (a country is never
      // written here).
      const city = pobCity.trim();
      const state = pobState.trim();
      const country = pobCountry.trim();
      const pobFull = pobDisplayName(city, state, country);
      // POB geocode — REAL coordinates (never the old 0,0 placeholder). Reuses
      // the same Nominatim geocoder as the intake People module. Falls back to
      // the coordinates already on file so a re-save never regresses to 0,0; tz
      // stays the AV no-DST default. Build A — the Contact POB longitude key is
      // `lon`, matching the engine + the person-hub doc (was `lng`).
      let pobObj: PlaceOfBirth | undefined;
      if (pobFull) {
        const geoCountry = country || 'India';
        // 1) Prefer the visible Coordinates box (auto-filled on blur, or a
        //    manually pasted "lat, lon" / DMS / Maps URL via parseCoordsString).
        const parsed = parseCoordsString(pobCoords);
        let lat: number | undefined = parsed?.lat;
        let lon: number | undefined = parsed?.lng; // geocode.ts uses `lng`; pob uses `lon`
        // 2) Fall back to a live geocode when the box is empty/unparseable —
        //    same Nominatim geocoder as the Intake People module (no new service).
        if (lat === undefined || lon === undefined) {
          const r = city
            ? await geocode({
                place: city,
                state: state || undefined,
                country: geoCountry,
                country_code: countryCodeHint(geoCountry),
              })
            : null;
          if (r) {
            lat = r.lat;
            lon = r.lon;
          }
        }
        // 3) Last resort — keep a real coordinate already on file so a re-save
        //    never regresses (0/0 on file counts as "no coordinate").
        if (
          (lat === undefined || lon === undefined) &&
          typeof client.pob?.lat === 'number' &&
          typeof client.pob?.lon === 'number' &&
          (client.pob.lat !== 0 || client.pob.lon !== 0)
        ) {
          lat = client.pob.lat;
          lon = client.pob.lon;
        }
        // 4) Still nothing — NEVER silently store 0/0. Warn and stop the save so
        //    staff can confirm/enter coordinates in the visible field.
        if (lat === undefined || lon === undefined) {
          setCoordWarn(
            "Couldn't locate that place — please enter coordinates (lat, lon) in the Coordinates field.",
          );
          return;
        }
        pobObj = {
          name: pobFull,
          city,
          state,
          country,
          lat,
          lon,
          tz: client.pob?.tz || 'Asia/Kolkata',
        };
      }
      const addrObj: ContactAddress = {
        street: addrStreet.trim() || undefined,
        city: addrCity.trim() || undefined,
        state: addrState.trim() || undefined,
        pin: addrPin.trim() || undefined,
        country: addrCountry.trim() || undefined,
      };
      const hasAddr = Object.values(addrObj).some(Boolean);
      await updateContactPersonData(cid, {
        dob: dob.trim() || undefined,
        tob: tob.trim() || undefined,
        pob: pobObj,
        prakriti: prakriti || undefined,
        health: health.trim(),
        address: hasAddr ? addrObj : null,
        referredBy: referredBy ?? null,
      });
      onSaved('Personal data saved.');
      setEditing(false);
    } catch (e) {
      onSaved(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!client) return null;

  const [pobCityR, pobStateR, pobCountryR] = readPobParts(client.pob);
  const stateCountry = [pobStateR, pobCountryR].filter(Boolean).join(', ');
  const prakritiOpt = PRAKRITI_OPTIONS.find((o) => o.code === client.prakriti);
  const hasPob = Boolean(pobCityR || stateCountry || client.pob?.name);

  // Coordinates — shown on ONE copy-pasteable line as `lat, lon` (matches the
  // editor field + intake/astro convention). ≤6dp, no trailing zeros. A legacy
  // 0/0 counts as "not captured" — never print "0, 0".
  const latN = typeof client.pob?.lat === 'number' ? client.pob.lat : null;
  const lonN = typeof client.pob?.lon === 'number' ? client.pob.lon : null;
  const hasCoords = latN !== null && lonN !== null && !(latN === 0 && lonN === 0);
  const fmtCoord = (n: number) => String(Number(n.toFixed(6)));
  const coordsText = hasCoords && latN !== null && lonN !== null
    ? `${fmtCoord(latN)}, ${fmtCoord(lonN)}`
    : null;

  return (
    <div>
      {!editing ? (
        <>
          <div className="block">
            <h2>Birth details</h2>
            <p className="sub">
              Used by the astro engine. Place of birth is structured
              (city / state / country + lat / lon / tz) so it stays portable
              across Contacts ↔ intake ↔ astro portal.
            </p>
            <div className="fields">
              <div className="f">
                <label>Date of birth (DOB)</label>
                <div className="val">{fmtDayMonthYear(client.dob) || client.dob || '—'}</div>
              </div>
              <div className="f">
                <label>Time of birth (TOB)</label>
                {/* VERBATIM: render the stored HH:MM string as-is. NEVER route
                    `tob` through new Date()/toLocaleTimeString/tz/LMT or any
                    solar (equation-of-time) correction — the astro engine owns
                    apparent-time conversion; this field mirrors the raw entry. */}
                <div className="val">{client.tob || '—'}</div>
              </div>
            </div>
          </div>

          <hr className="hr" />

          <div className="block">
            <h2>Place of birth (POB)</h2>
            {hasPob ? (
              <div className="fields">
                <div className="f">
                  <label>City</label>
                  <div className="val">{pobCityR || '—'}</div>
                </div>
                <div className="f">
                  <label>State / Country</label>
                  <div className="val">{stateCountry || '—'}</div>
                </div>
                <div className="f span2">
                  <label>Coordinates <span className="lon-key">(lat, lon)</span></label>
                  {coordsText ? (
                    <div className="val coords">{coordsText}</div>
                  ) : (
                    <div className="val note-inline">Not captured</div>
                  )}
                  <div className="note-inline" style={{ marginTop: 4 }}>
                    One line, copy-pasteable. Field key is <code>lon</code> (not{' '}
                    <code>lng</code>) — matches intake &amp; astro engine.
                  </div>
                </div>
                <div className="f">
                  <label>Time zone</label>
                  <div className="val">{client.pob?.tz || 'Asia/Kolkata (+05:30)'}</div>
                </div>
              </div>
            ) : (
              <p className="note-inline">Place of birth not captured yet.</p>
            )}
          </div>

          <hr className="hr" />

          <div className="block">
            <h2>Prakriti</h2>
            {client.prakriti ? (
              <div className="prakriti">
                <div className="pk-ico" aria-hidden="true">🌿</div>
                <div>
                  <div className="pk-val">{client.prakriti}</div>
                  <div className="pk-note">
                    {prakritiOpt ? prakritiOpt.desc : 'Ayurvedic constitution.'}
                  </div>
                </div>
              </div>
            ) : (
              <p className="note-inline">Prakriti not set.</p>
            )}
          </div>

          <hr className="hr" />

          <div className="block">
            <h2>Health notes</h2>
            <p className="sub">
              Personal health context that shapes the consultation — sits
              alongside the person’s birth &amp; constitution profile (context
              only, never a diagnosis).
            </p>
            {client.health && client.health.trim() ? (
              <p className="health-note">{client.health}</p>
            ) : (
              <p className="note-inline">No health notes on record.</p>
            )}
            <div style={{ marginTop: 14 }}>
              <Btn3 intent="ghost" size="sm" onClick={() => setEditing(true)}>
                Edit personal data
              </Btn3>
            </div>
          </div>
        </>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ flex: '1 1 140px' }}>
              <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}>
                Date of birth
              </label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label
                htmlFor="pd-tob"
                style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}
              >
                Time of birth (HH:MM, 24h, no DST)
              </label>
              {/* Fast mobile entry — type a dot for the colon (17.30 → 17:30),
                  or four digits (1730 → 17:30). Reuses the intake maskTime +
                  the astro dot→colon convention; stores the tight "HH:MM". */}
              <input
                id="pd-tob"
                type="text"
                inputMode="numeric"
                value={tob}
                onChange={(e) => setTob(maskTob(e.target.value))}
                placeholder="06:45"
                aria-invalid={tobInvalid || undefined}
                aria-describedby="pd-tob-hint"
                style={{
                  ...inputStyle,
                  borderColor: tobInvalid ? 'var(--warn, #9a6a12)' : 'var(--rule)',
                }}
              />
              <div
                id="pd-tob-hint"
                role={tobInvalid ? 'alert' : undefined}
                style={{
                  fontSize: 11,
                  marginTop: 4,
                  color: tobInvalid ? 'var(--warn, #9a6a12)' : 'var(--ink-mute)',
                  fontWeight: tobInvalid ? 600 : 400,
                }}
              >
                {tobInvalid
                  ? 'Enter a valid 24-hour time — 00–23 : 00–59.'
                  : 'Type a dot for the colon — 17.30 → 17:30.'}
              </div>
            </div>
          </div>

          {/* Fix 4 — POB: City + State + Country in a row. Auto-geocodes the
              Coordinates field on blur of the whole group (mirrors Intake). */}
          <div
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}
            onBlur={(e) => {
              // Only fire when focus leaves the whole City/State/Country group,
              // not when tabbing between its own fields.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                void autoGeocodePob();
              }
            }}
          >
            <div style={{ flex: '2 1 140px' }}>
              <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}>
                Place of birth
              </label>
              {/* City type-ahead — type ≥2 letters → pick a candidate → State,
                  Country and Coordinates auto-fill (structured, multi-result
                  Nominatim search). Manual typing without picking still works;
                  the blur-geocode below is the fallback. */}
              <CityAutocomplete
                value={pobCity}
                onChange={setPobCity}
                onSelect={onCityPicked}
                placeholder="City — type to search"
                ariaLabel="Birth city"
              />
            </div>
            <div style={{ flex: '2 1 120px' }}>
              <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}>
                &nbsp;
              </label>
              <input
                type="text"
                value={pobState}
                onChange={(e) => setPobState(e.target.value)}
                placeholder="State (optional)"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: '2 1 120px' }}>
              <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}>
                &nbsp;
              </label>
              <input
                type="text"
                value={pobCountry}
                onChange={(e) => setPobCountry(e.target.value)}
                placeholder="Country (optional)"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Coordinates — visible/confirmable, mirrors the Intake People module.
              Auto-fills from City + State + Country (same geocoder as the astro
              app); stays editable; a manually pasted "lat, lon" / DMS / Maps URL
              is parsed on save via parseCoordsString. */}
          <div style={{ marginBottom: 8 }}>
            <label
              htmlFor="pd-pob-coords"
              style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}
            >
              Coordinates{' '}
              <span style={{ color: 'var(--ink-mute)' }}>
                {geoBusy
                  ? '(fetching from City / State / Country…)'
                  : '(lat, lon — auto-filled from City / State / Country, editable)'}
              </span>
            </label>
            <input
              id="pd-pob-coords"
              type="text"
              value={pobCoords}
              onChange={(e) => {
                setPobCoords(e.target.value);
                if (coordWarn) setCoordWarn('');
              }}
              placeholder="Auto-fills from City / State / Country — e.g. 21.146633, 79.08886"
              style={inputStyle}
              aria-describedby={coordWarn ? 'pd-pob-coords-warn' : undefined}
            />
            {coordWarn && (
              <div
                id="pd-pob-coords-warn"
                role="alert"
                style={{ fontSize: 12, fontWeight: 600, color: 'var(--warn, #9a6a12)', marginTop: 4 }}
              >
                {coordWarn}
              </div>
            )}
          </div>

          {/* Prakriti — permanent attribute, always editable, never TOB-dependent */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Prakriti
            </label>
            <select
              value={prakriti}
              onChange={(e) => setPrakriti(e.target.value)}
              style={{ ...inputStyle, maxWidth: 420 }}
            >
              <option value="">— Select Prakriti —</option>
              {PRAKRITI_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.code} — {o.desc}
                </option>
              ))}
            </select>
            {/* Show the selected description below the dropdown (always visible without opening it) */}
            {prakriti && (() => {
              const opt = PRAKRITI_OPTIONS.find((o) => o.code === prakriti);
              return opt ? (
                <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 4 }}>
                  {opt.code} · {opt.desc}
                </div>
              ) : null;
            })()}
          </div>

          {/* Health — per-person context read alongside astrology (portal-side PII) */}
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="pd-health" style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Health
            </label>
            <textarea
              id="pd-health"
              value={health}
              onChange={(e) => setHealth(e.target.value)}
              placeholder="Health concerns read alongside astrology (context only — never a diagnosis)"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 44 }}
            />
          </div>

          {/* Fix 2 — Address */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Address
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input
                type="text"
                value={addrStreet}
                onChange={(e) => setAddrStreet(e.target.value)}
                placeholder="Street, locality, area"
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={addrCity}
                  onChange={(e) => setAddrCity(e.target.value)}
                  placeholder="City"
                  style={{ ...inputStyle, flex: '2 1 120px' }}
                />
                <input
                  type="text"
                  value={addrState}
                  onChange={(e) => setAddrState(e.target.value)}
                  placeholder="State"
                  style={{ ...inputStyle, flex: '2 1 120px' }}
                />
                <input
                  type="text"
                  value={addrPin}
                  onChange={(e) => setAddrPin(e.target.value)}
                  placeholder="PIN / ZIP"
                  style={{ ...inputStyle, flex: '1 1 90px' }}
                />
                <input
                  type="text"
                  value={addrCountry}
                  onChange={(e) => setAddrCountry(e.target.value)}
                  placeholder="Country"
                  style={{ ...inputStyle, flex: '2 1 120px' }}
                />
              </div>
              {/* Copy address button — shown only when address data is present */}
              {[addrStreet, addrCity, addrState, addrPin, addrCountry].some(Boolean) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <button
                    type="button"
                    onClick={copyAddress}
                    style={{
                      fontSize: 12,
                      color: 'var(--accent, #4f7cff)',
                      background: 'none',
                      border: '1px solid var(--rule)',
                      borderRadius: 'var(--r-sm)',
                      padding: '2px 8px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                    title="Copy mailing-format address to clipboard"
                  >
                    Copy address
                  </button>
                  {copyStatus && (
                    <span style={{ fontSize: 12, color: 'var(--good, #28a745)' }}>
                      {copyStatus}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Fix 3 — Referred by typeahead */}
          <div style={{ marginBottom: 12, position: 'relative' }}>
            <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Referred by
            </label>
            {referredBy ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--ink)' }}>
                  {referredBy.name}
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)', marginLeft: 6 }}>
                    {referredBy.contactId}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => { setReferredBy(undefined); setRefSearch(''); }}
                  style={{ fontSize: 12, color: 'var(--ink-mute)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
                  aria-label="Clear referral"
                  title="Clear referral"
                >
                  ×
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={refSearch}
                  onChange={(e) => { setRefSearch(e.target.value); setRefDropdown(true); }}
                  onFocus={() => setRefDropdown(true)}
                  onBlur={() => setTimeout(() => setRefDropdown(false), 150)}
                  placeholder="Type name or C#####…"
                  style={inputStyle}
                />
                {refDropdown && refMatches.length > 0 && (
                  <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', marginTop: 2, background: 'var(--surface)', position: 'absolute', zIndex: 10, width: '100%', maxWidth: 360 }}>
                    {refMatches.map((c) => (
                      <button
                        key={c.cid}
                        type="button"
                        onMouseDown={() => {
                          setReferredBy({ contactId: c.cid, name: c.name });
                          setRefSearch(`${c.name} · ${c.cid}`);
                          setRefDropdown(false);
                        }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid var(--rule)' }}
                      >
                        <b>{c.name}</b>{' '}
                        <span style={{ color: 'var(--ink-mute)', fontSize: 12 }}>{c.cid}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn3 intent="grn" size="sm" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save personal data'}
            </Btn3>
            <Btn3 intent="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Btn3>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════ Fix 5 — Notes section ════════════════════
 *
 * Notes live in the `clients/{id}/notes/` subcollection.
 * Append-only for staff. Ordered by createdAt desc, max 50 loaded, 10 shown.
 */

interface NoteEntry {
  id: string;
  text: string;
  createdBy: string;
  createdAt: Date | null;
  type: string;
}

function NotesSection({
  cid,
  actorName,
}: {
  cid: string;
  actorName: string;
}) {
  const [notes, setNotes] = useState<NoteEntry[]>([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // Load notes from subcollection
  useEffect(() => {
    if (!cid) return;
    let alive = true;
    getDocs(
      fsQuery(
        collection(db, 'clients', cid, 'notes'),
        orderBy('createdAt', 'desc'),
      ),
    )
      .then((snap) => {
        if (!alive) return;
        const loaded: NoteEntry[] = snap.docs.slice(0, 50).map((d) => {
          const data = d.data();
          return {
            id: d.id,
            text: String(data.text ?? ''),
            createdBy: String(data.createdBy ?? ''),
            createdAt: data.createdAt?.toDate?.() ?? null,
            type: String(data.type ?? 'manual'),
          };
        });
        setNotes(loaded);
        setNotesLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setNotesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [cid]);

  async function saveNote() {
    const text = noteText.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      const ref = await addDoc(collection(db, 'clients', cid, 'notes'), {
        text,
        createdBy: actorName,
        createdAt: serverTimestamp(),
        type: 'manual',
      });
      const newNote: NoteEntry = {
        id: ref.id,
        text,
        createdBy: actorName,
        createdAt: new Date(),
        type: 'manual',
      };
      setNotes((prev) => [newNote, ...prev]);
      setNoteText('');
      toast('Note saved.');
    } catch (e) {
      toast(`Could not save note: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  function formatNoteDate(d: Date | null): string {
    if (!d) return '—';
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  const visibleNotes = showAll ? notes : notes.slice(0, 10);

  return (
    <div style={{ marginTop: 16 }}>
      <Lbl>Notes</Lbl>

      {/* Add note form */}
      <div style={{ marginBottom: 12 }}>
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add a note…"
          rows={3}
          style={{
            ...inputStyle,
            resize: 'vertical',
            marginBottom: 6,
          }}
        />
        <Btn3 intent="grn" size="sm" onClick={saveNote} disabled={saving || !noteText.trim()}>
          {saving ? 'Saving…' : 'Save note'}
        </Btn3>
      </div>

      {/* Notes list */}
      {notesLoading ? (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Loading notes…</p>
      ) : notes.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No notes yet.</p>
      ) : (
        <>
          {visibleNotes.map((n) => (
            <div
              key={n.id}
              style={{
                borderLeft: '3px solid var(--rule-strong, #ddd)',
                paddingLeft: 10,
                marginBottom: 10,
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginBottom: 2 }}>
                {formatNoteDate(n.createdAt)} · {n.createdBy}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', whiteSpace: 'pre-wrap' }}>
                {n.text}
              </div>
            </div>
          ))}
          {notes.length > 10 && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              style={{
                fontSize: 12,
                color: 'var(--accent, #4f7cff)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Show all {notes.length} notes
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ════════════════════ Fix 6+7 — Roles section ════════════════════
 *
 * Shows all projects where this contact has a role (via participants[] or
 * legacy clientId/partnerId). Includes an inline "+ Assign to project" picker.
 */

function RolesSection({
  cid,
  liveProjects,
  actorName,
}: {
  cid: string;
  liveProjects: LiveProject[];
  actorName: string;
}) {
  const { toast } = useToast();
  const [assigning, setAssigning] = useState(false);
  const [assignPid, setAssignPid] = useState('');
  const [assignRole, setAssignRole] = useState<ParticipantRole>('co_client');
  const [assignRelationship, setAssignRelationship] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const assignSearchRef = useRef<HTMLInputElement>(null);

  // Build role list from liveProjects for this contact
  const roles = useMemo(() => {
    const result: Array<{
      pid: string;
      vp: string;
      projectName: string;
      tierCode: string;
      role: ParticipantRole | 'legacy_client' | 'legacy_partner';
      relationship?: string;
    }> = [];
    for (const p of liveProjects) {
      if (p.trashed) continue;
      if (p.participants && p.participants.length > 0) {
        for (const entry of p.participants) {
          if (entry.contactId === cid) {
            result.push({
              pid: p.pid,
              vp: p.vp,
              projectName: p.projectName,
              tierCode: p.tierCode,
              role: entry.role,
              relationship: entry.relationship,
            });
          }
        }
      } else {
        // Legacy path
        if ((p.clientId || p.clientNo) === cid) {
          result.push({ pid: p.pid, vp: p.vp, projectName: p.projectName, tierCode: p.tierCode, role: 'legacy_client' });
        }
        if (p.partnerId === cid) {
          result.push({ pid: p.pid, vp: p.vp, projectName: p.projectName, tierCode: p.tierCode, role: 'legacy_partner' });
        }
      }
    }
    return result;
  }, [liveProjects, cid]);

  // Projects visible in the assign picker
  const filteredProjects = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    if (!q) return liveProjects.filter((p) => !p.trashed && !p.archived).slice(0, 20);
    return liveProjects
      .filter((p) => !p.trashed)
      .filter((p) =>
        p.vp.toLowerCase().includes(q) ||
        (p.projectName ?? '').toLowerCase().includes(q) ||
        (p.clientName ?? '').toLowerCase().includes(q),
      )
      .slice(0, 20);
  }, [liveProjects, assignSearch]);

  // Is the contact already on the selected project?
  const alreadyOnProject = useMemo(() => {
    if (!assignPid) return null;
    const existing = roles.find((r) => r.pid === assignPid);
    return existing ?? null;
  }, [roles, assignPid]);

  async function doAssign() {
    if (!assignPid || busy) return;
    setBusy(true);
    try {
      await addParticipant(assignPid, {
        contactId: cid,
        role: assignRole,
        relationship: assignRelationship.trim() || undefined,
        addedAt: new Date().toISOString(),
        addedBy: actorName,
      });
      toast(`Assigned to ${liveProjects.find((p) => p.pid === assignPid)?.vp ?? assignPid} as ${participantLabel(assignRole)}.`);
      setAssigning(false);
      setAssignPid('');
      setAssignSearch('');
      setAssignRelationship('');
    } catch (e) {
      toast(`Assign failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function roleBadgeLabel(role: string): string {
    const MAP: Record<string, string> = {
      primary_client: 'Primary Client',
      co_client: 'Co-Client',
      family: 'Family Member',
      consultant: 'Consultant',
      partner: 'Channel Partner',
      staff: 'AV Staff',
      legacy_client: 'Client (legacy)',
      legacy_partner: 'Partner (legacy)',
    };
    return MAP[role] ?? role;
  }

  return (
    <Box title="Roles">
      {roles.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--ink-mute)', margin: '0 0 12px' }}>
          No roles assigned yet — add this contact to a project as a participant.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {roles.map((r, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--ink-mute)', minWidth: 60 }}>
                {r.vp}
              </span>
              <span style={{ fontSize: 13, color: 'var(--ink)', flex: 1 }}>
                {r.projectName || r.vp}
              </span>
              <Tag kind="val">{roleBadgeLabel(r.role)}</Tag>
              {r.relationship && (
                <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                  ({r.relationship})
                </span>
              )}
              <Link to={`/project/${r.pid}`} style={{ fontSize: 12 }}>
                Open →
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Fix 7 — Assign to project inline picker */}
      {!assigning ? (
        <Btn3 intent="ghost" size="sm" onClick={() => { setAssigning(true); setTimeout(() => assignSearchRef.current?.focus(), 50); }}>
          + Assign to project
        </Btn3>
      ) : (
        <Card flat style={{ marginTop: 8, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-mute)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Assign to project
          </div>
          {/* Project search */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}>
              Project (VP# or name)
            </label>
            <input
              ref={assignSearchRef}
              type="text"
              value={assignSearch}
              onChange={(e) => { setAssignSearch(e.target.value); setAssignPid(''); }}
              placeholder="Type VP# or project name…"
              style={inputStyle}
            />
            {assignSearch && filteredProjects.length > 0 && !assignPid && (
              <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', marginTop: 2, maxHeight: 160, overflowY: 'auto', background: 'var(--surface)' }}>
                {filteredProjects.map((p) => (
                  <button
                    key={p.pid}
                    type="button"
                    onClick={() => { setAssignPid(p.pid); setAssignSearch(`${p.vp} — ${p.projectName || p.clientName}`); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid var(--rule)' }}
                  >
                    <b>{p.vp}</b>{' '}
                    <span style={{ color: 'var(--ink-soft)' }}>{p.projectName || p.clientName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Guard: already on this project */}
          {alreadyOnProject && (
            <p style={{ fontSize: 12, color: 'var(--ink-soft)', borderLeft: '3px solid var(--accent)', paddingLeft: 8, margin: '0 0 8px' }}>
              Already on this project as <b>{roleBadgeLabel(alreadyOnProject.role)}</b>.
            </p>
          )}

          {/* Role picker */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}>
                Role
              </label>
              <select
                value={assignRole}
                onChange={(e) => setAssignRole(e.target.value as ParticipantRole)}
                style={inputStyle}
              >
                <option value="primary_client">Primary Client</option>
                <option value="co_client">Co-Client</option>
                <option value="family">Family Member</option>
                <option value="consultant">Consultant</option>
                <option value="partner">Channel Partner</option>
                <option value="staff">AV Staff</option>
              </select>
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ fontSize: 11, color: 'var(--ink-mute)', display: 'block', marginBottom: 4 }}>
                Relationship (optional)
              </label>
              <input
                type="text"
                value={assignRelationship}
                onChange={(e) => setAssignRelationship(e.target.value)}
                placeholder="e.g. spouse, architect"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn3
              intent="grn"
              size="sm"
              onClick={doAssign}
              disabled={busy || !assignPid || !!alreadyOnProject}
            >
              {busy ? 'Assigning…' : 'Assign'}
            </Btn3>
            <Btn3 intent="ghost" size="sm" onClick={() => { setAssigning(false); setAssignPid(''); setAssignSearch(''); setAssignRelationship(''); }} disabled={busy}>
              Cancel
            </Btn3>
          </div>
        </Card>
      )}
    </Box>
  );
}

/* ════════════════════ Groups Section ════════════════════
 *
 * Module 2 — Family/Organisation group membership for this contact.
 *
 * Display (read):
 *   Lists all groups this contact belongs to. Each row: group name · type badge
 *   · relationship label · "Open group →" link.  If none: "Not part of any group."
 *
 * Assign to group:
 *   "+ Add to group" link → inline picker (search existing groups by name OR
 *   "+ Create new group" link that navigates to /families).
 *   Optional relationship label → calls addMemberToFamily().
 *
 * Remove from group:
 *   × button → calls removeMemberFromFamily().
 */
function GroupsSection({
  cid,
  contactName,
  liveContacts,
  actorName,
}: {
  cid: string;
  contactName: string;
  liveContacts: LiveClient[];
  actorName: string;
}) {
  const { toast } = useToast();
  const [allGroups, setAllGroups] = useState<FamilyGroup[]>([]);
  const [adding, setAdding] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<FamilyGroup | null>(null);
  const [relationship, setRelationship] = useState('');
  const [busy, setBusy] = useState(false);

  void liveContacts; // used by parent to thread masters — not needed here directly

  useEffect(() => {
    const unsub = listenFamilies(setAllGroups);
    return unsub;
  }, []);

  // Groups this contact belongs to
  const myGroups = useMemo(
    () => allGroups.filter((g) => g.members.some((m) => m.contactId === cid)),
    [allGroups, cid],
  );

  // Groups NOT already containing this contact (for the picker)
  const candidateGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    return allGroups
      .filter((g) => !g.members.some((m) => m.contactId === cid))
      .filter((g) => !q || g.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [allGroups, cid, groupSearch]);

  // Relationship label stored for this contact in a given group
  function myRelationshipIn(group: FamilyGroup): string | undefined {
    return group.members.find((m) => m.contactId === cid)?.relationship;
  }

  async function addToGroup() {
    if (!selectedGroup || busy) return;
    setBusy(true);
    try {
      const member: FamilyMember = {
        contactId: cid,
        name: contactName,
        relationship: relationship.trim() || undefined,
      };
      await addMemberToFamily(selectedGroup.id, member, actorName);
      toast(`Added to "${selectedGroup.name}".`);
      setAdding(false);
      setSelectedGroup(null);
      setGroupSearch('');
      setRelationship('');
    } catch (e) {
      toast(`Add failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function removeFromGroup(group: FamilyGroup) {
    try {
      await removeMemberFromFamily(group.id, cid, group.members, actorName);
      toast(`Removed from "${group.name}".`);
    } catch (e) {
      toast(`Remove failed: ${(e as Error).message}`);
    }
  }

  return (
    <>
      {myGroups.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--ink-mute)', margin: '0 0 12px' }}>
          Not part of any group yet.
        </p>
      ) : (
        <div className="grouplist" style={{ marginBottom: 12 }}>
          {myGroups.map((g) => {
            const rel = myRelationshipIn(g);
            const isFamily = g.type === 'family';
            return (
              <div key={g.id} style={{ position: 'relative' }}>
                <Link to="/families" className="glink" title={`Open ${g.name}`}>
                  <span
                    className={`gicon ${isFamily ? 'fam' : 'org'}`}
                    aria-hidden="true"
                  >
                    {isFamily ? '👪' : '🏢'}
                  </span>
                  <span className="gtext">
                    <span className="gname">{g.name}</span>
                    <div className="grole">
                      {isFamily ? 'Family' : 'Organisation'}
                      {rel && (
                        <>
                          {' · relationship: '}
                          <b>{rel}</b>
                        </>
                      )}
                    </div>
                  </span>
                  <span className="chev" aria-hidden="true">›</span>
                </Link>
                {/* Remove stays a wired control — top-right, clear of the chevron. */}
                <button
                  type="button"
                  onClick={() => void removeFromGroup(g)}
                  title={`Remove from ${g.name}`}
                  aria-label={`Remove from ${g.name}`}
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 8,
                    background: 'var(--surface)',
                    border: '1px solid var(--rule)',
                    borderRadius: 'var(--r-pill)',
                    width: 22,
                    height: 22,
                    lineHeight: '18px',
                    cursor: 'pointer',
                    color: 'var(--ink-mute)',
                    fontSize: 13,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign to group */}
      {!adding ? (
        <Btn3 intent="ghost" size="sm" onClick={() => setAdding(true)}>
          + Add to group
        </Btn3>
      ) : (
        <Card flat style={{ marginTop: 10, padding: '12px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-mute)', marginBottom: 8 }}>
            SEARCH GROUP
          </div>
          <div style={{ position: 'relative' }}>
            <input
              value={selectedGroup ? selectedGroup.name : groupSearch}
              onChange={(e) => { setGroupSearch(e.target.value); setSelectedGroup(null); }}
              placeholder="Search group by name…"
              style={{
                border: '1px solid var(--rule)',
                borderRadius: 'var(--r-sm)',
                padding: '8px 10px',
                fontSize: 13.5,
                fontFamily: 'inherit',
                color: 'var(--ink)',
                background: 'var(--surface)',
                width: '100%',
              }}
              autoFocus
            />
            {selectedGroup && (
              <button
                type="button"
                onClick={() => { setSelectedGroup(null); setGroupSearch(''); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-mute)', fontSize: 14 }}
              >
                ×
              </button>
            )}
            {!selectedGroup && candidateGroups.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'var(--surface)',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r-sm)',
                  zIndex: 50,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                {candidateGroups.map((g) => (
                  <div
                    key={g.id}
                    onMouseDown={() => { setSelectedGroup(g); setGroupSearch(g.name); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--rule-light, #eee)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <span style={{ flex: 1 }}>{g.name}</span>
                    <Tag kind="val">
                      {g.type === 'family' ? 'Family' : 'Org'}
                    </Tag>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginTop: 8 }}>
            <input
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              placeholder="Relationship (optional, e.g. husband, CFO)"
              style={{
                border: '1px solid var(--rule)',
                borderRadius: 'var(--r-sm)',
                padding: '8px 10px',
                fontSize: 13.5,
                fontFamily: 'inherit',
                color: 'var(--ink)',
                background: 'var(--surface)',
                width: '100%',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn3
              intent="grn"
              size="sm"
              onClick={addToGroup}
              disabled={!selectedGroup || busy}
            >
              {busy ? 'Adding…' : 'Add to group'}
            </Btn3>
            <Btn3 intent="ghost" size="sm" onClick={() => { setAdding(false); setSelectedGroup(null); setGroupSearch(''); setRelationship(''); }}>
              Cancel
            </Btn3>
            <Link to="/families" style={{ fontSize: 12.5, marginLeft: 4 }}>
              Create new group →
            </Link>
          </div>
        </Card>
      )}
    </>
  );
}

/* ContactCommsSidebar removed in M3.2 — replaced by ContactThreadPanel (see above). */
